import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'

const API_URL = process.env.REACT_APP_API_URL

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function parseAdditionalCcEmails(value) {
  return String(value || '')
    .split(/[,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
}

function isValidEmailAddress(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim())
}

function buildCcEmails(userEmail, additionalInput) {
  const result = []
  const seen = new Set()
  const addEmail = (email) => {
    const trimmed = String(email || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  }

  addEmail(userEmail)
  parseAdditionalCcEmails(additionalInput).forEach(addEmail)
  return result
}

/** Presigned URL for View PDF. Do not open private pdfLink directly. */
function getLrfqPdfPreviewUrl(payload) {
  if (!payload || typeof payload !== 'object') return ''
  return String(payload.pdfDownloadUrl || payload.downloadUrl || '').trim()
}

function hasLrfqPdfDocument(payload) {
  if (!payload || typeof payload !== 'object') return false
  return Boolean(
    getLrfqPdfPreviewUrl(payload) || String(payload.pdfLink || '').trim()
  )
}

function pickLrfqFromResponse(responseData, id) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.lrfq && typeof dataBlock.lrfq === 'object') {
    return dataBlock.lrfq
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.lrfqs == null &&
    (dataBlock._id != null ||
      dataBlock.lrfqNumber != null ||
      dataBlock.LrfqNumber != null ||
      dataBlock.lrfqId != null)
  ) {
    return dataBlock
  }

  const list = dataBlock.lrfqs
  if (!Array.isArray(list) || list.length === 0) return null
  const found = list.find((r) => String(r._id || r.id) === String(id))
  return found || list[0]
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str.replace(/-/g, '/')
  const dateTimeMatch = str.match(/^(\d{2})-(\d{2})-(\d{4})[ T]/)
  if (dateTimeMatch) {
    return `${dateTimeMatch[1]}/${dateTimeMatch[2]}/${dateTimeMatch[3]}`
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return formatDateParts(d)
}

function formatDateParts(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  return `${get('day')}/${get('month')}/${get('year')}`
}

function parseLrfqDueDate(value) {
  if (!value) return null
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split('-')
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), 23, 59, 59, 999)
  }
  if (/^\d{2}-\d{2}-\d{4}[ T]\d{1,2}:\d{2}/.test(str)) {
    const [datePart, timePart] = str.split(/[ T]/)
    const [dd, mm, yyyy] = datePart.split('-')
    const [hh, min] = timePart.split(':')
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      59,
      999
    )
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

/** True once the LRFQ due date/time has passed (target-price updates allowed). */
function isLrfqDueDatePassed(dueDate) {
  const due = parseLrfqDueDate(dueDate)
  if (!due) return false
  return Date.now() > due.getTime()
}

function isTargetPriceDueDateActive(value) {
  const due = parseLrfqDueDate(value)
  if (!due) return false
  return Date.now() <= due.getTime()
}

function getSupplierTargetPriceDueDate(supplier) {
  if (!supplier || typeof supplier !== 'object') return ''
  return String(
    supplier.targetPriceDueDate ||
      supplier.priceMatchingDueDate ||
      supplier.targetDueDate ||
      ''
  ).trim()
}

const TARGET_PRICE_BEFORE_DUE_MSG =
  'Target price can be updated only after the LRFQ due date is completed'

function formatDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  // API often returns "DD-MM-YYYY HH:mm" (e.g. 30-10-2026 20:00)
  const localMatch = str.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?/
  )
  if (localMatch) {
    const [, dd, mm, yyyy, hh = '', min = ''] = localMatch
    if (!hh) return `${dd}/${mm}/${yyyy}`
    const hourNum = Number(hh)
    const period = hourNum >= 12 ? 'PM' : 'AM'
    const hour12 = hourNum % 12 || 12
    return `${dd}/${mm}/${yyyy}, ${String(hour12).padStart(2, '0')}:${min} ${period}`
  }

  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'

  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).formatToParts(d)

  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')} ${dayPeriod}`
}

function displayUser(ref) {
  if (!ref) return '-'
  if (typeof ref === 'string') return ref
  if (ref.name) return ref.name
  if (ref.email) return ref.email
  return '-'
}

function capitalizeStatus(value) {
  if (!value) return '-'
  const s = String(value)
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function formatAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function isDecimalInput(value) {
  return value === '' || /^\d*\.?\d*$/.test(String(value))
}

function parseDecimalValue(value) {
  if (value == null || value === '') return NaN
  const num = Number(String(value).replace(/,/g, '').trim())
  return Number.isFinite(num) ? num : NaN
}

function getMaterialBoqLines(material) {
  if (Array.isArray(material?.boq) && material.boq.length > 0) {
    return material.boq
  }
  if (Array.isArray(material?.boqItems) && material.boqItems.length > 0) {
    return material.boqItems
  }
  return []
}

function hasDisplayQuantity(value) {
  if (value == null) return false
  const text = String(value).trim()
  if (text === '' || text === '-') return false
  return true
}

function findLrfqMaterialForQuote(materials, quote, quoteIndex) {
  const quoteId = getBoqLineId(quote)
  if (quoteId) {
    const byId = materials.find((m) => getBoqLineId(m) === quoteId)
    if (byId) return byId
  }
  return materials[quoteIndex] || null
}

function findLrfqBoqLane(materialLanes, lane, laneIndex, parentNo) {
  const laneId = getBoqLineId(lane)
  const laneNo = getBoqLineNo(lane, `${parentNo}.${laneIndex + 1}`)
  if (laneId) {
    const byId = materialLanes.find((m) => getBoqLineId(m) === laneId)
    if (byId) return byId
  }
  if (laneNo) {
    const byNo = materialLanes.find((m) => getBoqLineNo(m) === laneNo)
    if (byNo) return byNo
  }
  return materialLanes[laneIndex] || null
}

/**
 * materialQuotes.boq often omits quantity; LRFQ materials.boqItems has it.
 * Build lanes from materials (qty/logistics) and overlay quoted prices.
 */
function enrichMaterialQuotesWithLrfqBoq(quotes, lrfq) {
  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : []
  if (!Array.isArray(quotes) || quotes.length === 0) {
    return Array.isArray(quotes) ? quotes : []
  }
  if (materials.length === 0) return quotes

  return quotes.map((quote, quoteIndex) => {
    const material = findLrfqMaterialForQuote(materials, quote, quoteIndex)
    const materialLanes = getMaterialBoqLines(material)
    const quoteLanes = getMaterialBoqLines(quote)
    if (materialLanes.length === 0 && quoteLanes.length === 0) return quote

    const parentNo = quoteIndex + 1
    // Prefer material lanes as the source of quantity / logistics.
    const sourceLanes =
      materialLanes.length > 0 ? materialLanes : quoteLanes

    const enrichedBoq = sourceLanes.map((baseLane, laneIndex) => {
      const quotedLane =
        findLrfqBoqLane(quoteLanes, baseLane, laneIndex, parentNo) ||
        quoteLanes[laneIndex] ||
        null
      const materialLane =
        materialLanes.length > 0
          ? baseLane
          : findLrfqBoqLane(materialLanes, baseLane, laneIndex, parentNo)

      return {
        ...(materialLane || {}),
        ...(quotedLane || {}),
        ...(baseLane || {}),
        uniqueId:
          quotedLane?.uniqueId ||
          materialLane?.uniqueId ||
          baseLane?.uniqueId ||
          '',
        sNo:
          quotedLane?.sNo ||
          materialLane?.sNo ||
          baseLane?.sNo ||
          `${parentNo}.${laneIndex + 1}`,
        quantity: hasDisplayQuantity(quotedLane?.quantity)
          ? quotedLane.quantity
          : hasDisplayQuantity(materialLane?.quantity)
            ? materialLane.quantity
            : hasDisplayQuantity(baseLane?.quantity)
              ? baseLane.quantity
              : '',
        basicPrice:
          quotedLane?.basicPrice ??
          materialLane?.basicPrice ??
          baseLane?.basicPrice,
        comments:
          quotedLane?.comments ||
          quotedLane?.technicalResponse ||
          '',
        sourcePincode:
          quotedLane?.sourcePincode ||
          materialLane?.sourcePincode ||
          baseLane?.sourcePincode,
        destinationPincode:
          quotedLane?.destinationPincode ||
          materialLane?.destinationPincode ||
          baseLane?.destinationPincode,
        sourceCity:
          quotedLane?.sourceCity ||
          materialLane?.sourceCity ||
          baseLane?.sourceCity,
        destinationCity:
          quotedLane?.destinationCity ||
          materialLane?.destinationCity ||
          baseLane?.destinationCity,
        truckName:
          quotedLane?.truckName ||
          materialLane?.truckName ||
          baseLane?.truckName,
        truckBody:
          quotedLane?.truckBody ||
          materialLane?.truckBody ||
          baseLane?.truckBody,
        truckLoadabilityMt:
          hasDisplayQuantity(materialLane?.truckLoadabilityMt) &&
          String(quotedLane?.truckLoadabilityMt) === '0'
            ? materialLane.truckLoadabilityMt
            : quotedLane?.truckLoadabilityMt ??
              materialLane?.truckLoadabilityMt ??
              baseLane?.truckLoadabilityMt,
        truckLengthFt:
          quotedLane?.truckLengthFt ??
          materialLane?.truckLengthFt ??
          baseLane?.truckLengthFt,
        truckWidthFt:
          quotedLane?.truckWidthFt ??
          materialLane?.truckWidthFt ??
          baseLane?.truckWidthFt,
        truckHeightFt:
          quotedLane?.truckHeightFt ??
          materialLane?.truckHeightFt ??
          baseLane?.truckHeightFt,
      }
    })

    return {
      ...quote,
      boq: enrichedBoq,
    }
  })
}

/** Display Qty: quoted value, else matching LRFQ materials.boqItems quantity. */
function resolveBoqQuantityForDisplay(boqItem, lrfq) {
  if (hasDisplayQuantity(boqItem?.quantity)) return boqItem.quantity
  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : []
  const laneId = getBoqLineId(boqItem)
  const laneNo = getBoqLineNo(boqItem)
  for (const material of materials) {
    for (const lane of getMaterialBoqLines(material)) {
      const sameId = laneId && getBoqLineId(lane) === laneId
      const sameNo = laneNo && getBoqLineNo(lane) === laneNo
      if ((sameId || sameNo) && hasDisplayQuantity(lane.quantity)) {
        return lane.quantity
      }
    }
  }
  return '-'
}

function getBoqLineId(item) {
  return String(item?.uniqueId || item?.boq_id || item?._id || item?.id || '').trim()
}

function getBoqLineNo(item, fallback = '') {
  return String(item?.sNo || item?.boqNo || fallback || '').trim()
}

/** Quote lines to compare: nested lanes when present, else the material quote itself. */
function getQuoteLines(quote) {
  const lanes = getMaterialBoqLines(quote)
  return lanes.length > 0 ? lanes : quote && typeof quote === 'object' ? [quote] : []
}

/** e.g. "Mumbai → Delhi · 32 Ft MXL" */
function getLaneLabel(lane) {
  const source = lane?.sourceCity || lane?.sourcePincode || ''
  const destination = lane?.destinationCity || lane?.destinationPincode || ''
  const route = source || destination ? `${source || '-'} → ${destination || '-'}` : ''
  return [route, lane?.truckName || ''].filter(Boolean).join(' · ') || '-'
}

/** lrfq.targetPrice is an array of { uniqueId, targetPrice }. */
function getLrfqTargetMap(lrfq) {
  const map = {}
  if (!Array.isArray(lrfq?.targetPrice)) return map
  lrfq.targetPrice.forEach((item) => {
    const uniqueId = String(item?.uniqueId || '').trim()
    const price = Number(item?.targetPrice)
    if (uniqueId && Number.isFinite(price)) map[uniqueId] = price
  })
  return map
}

/** Flatten LRFQ materials / BOQ lanes into target-price rows for the Price Matching form. */
function collectLrfqBoqTargetRows(lrfq) {
  const materials = Array.isArray(lrfq?.materials) ? lrfq.materials : []
  const targetMap = getLrfqTargetMap(lrfq)
  const rows = []
  const pushRow = (item, boqNo, label, parentDescription, material) => {
    const uniqueId = getBoqLineId(item)
    const key = uniqueId || `no:${boqNo}`
    rows.push({
      key,
      boq_id: uniqueId,
      boqNo,
      materialDescription: label,
      parentDescription,
      quantity: material.quantity,
      uom: material.uom || '',
      currentTarget:
        uniqueId && targetMap[uniqueId] != null ? String(targetMap[uniqueId]) : ''
    })
  }
  materials.forEach((material, matIndex) => {
    const lineNo = matIndex + 1
    const lanes = getMaterialBoqLines(material)
    const parentLabel = String(
      material.sourceDescription || material.sourceCode || ''
    ).trim()
    const parentDescription =
      parentLabel && parentLabel !== '-' ? parentLabel : `Line ${lineNo}`
    if (lanes.length === 0) {
      if (!getBoqLineId(material)) return
      pushRow(
        material,
        String(lineNo),
        material.sourceDescription || material.sourceCode || '-',
        '',
        material
      )
      return
    }
    lanes.forEach((lane, laneIndex) => {
      pushRow(
        lane,
        getBoqLineNo(lane, `${lineNo}.${laneIndex + 1}`),
        getLaneLabel(lane),
        parentDescription,
        material
      )
    })
  })
  return rows
}

function getSupplierBoqTargetValueMap(supplier) {
  const map = {}
  const quotes = Array.isArray(supplier?.materialQuotes)
    ? supplier.materialQuotes
    : []
  quotes.forEach((quote) => {
    getQuoteLines(quote).forEach((item) => {
      const effective = getLineEffectiveTargetPrice(item)
      if (!Number.isFinite(effective) || effective <= 0) {
        if (item?.targetPrice == null || item.targetPrice === '') return
      }
      const value = Number.isFinite(effective)
        ? String(effective)
        : String(item.targetPrice)
      const id = getBoqLineId(item)
      const no = getBoqLineNo(item)
      if (id) map[`id:${id}`] = value
      if (no) map[`no:${no}`] = value
    })
  })
  return map
}

function buildTargetInputsFromLrfq(lrfq, supplier = null) {
  const rows = collectLrfqBoqTargetRows(lrfq)
  const supplierMap = supplier ? getSupplierBoqTargetValueMap(supplier) : {}
  const inputs = {}
  rows.forEach((row) => {
    const fromSupplier =
      (row.boq_id && supplierMap[`id:${row.boq_id}`]) ||
      (row.boqNo && supplierMap[`no:${row.boqNo}`]) ||
      ''
    inputs[row.key] = fromSupplier || row.currentTarget || ''
  })
  return inputs
}

function displayQuoteHsn(quote) {
  const value = String(quote?.hsn || quote?.hsnCode || '').trim()
  return value || '-'
}

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function getSupplierAttachments(supplier) {
  if (!Array.isArray(supplier?.attachments)) return []
  return supplier.attachments.map(mapAttachmentFromApi).filter((att) => att.s3Key)
}

const AttachmentTileGrid = ({ attachments, downloadingS3Key, onDownload, disabled }) => {
  if (!attachments?.length) return null

  return (
    <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const fileMeta = getFileTypeMeta(
          getFileExtension(fileName, att.contentType),
          att.contentType
        )
        const isDownloading = downloadingS3Key === att.s3Key
        return (
          <button
            key={att.s3Key}
            type="button"
            className="border-0 text-center"
            title={`Open ${fileName}`}
            onClick={() => onDownload(att.s3Key, att.url, att.originalFileName)}
            disabled={disabled || isDownloading}
            style={{
              width: '92px',
              padding: '10px 8px',
              borderRadius: '10px',
              backgroundColor: fileMeta.bg,
              border: `1px solid ${fileMeta.color}33`,
              cursor: disabled || isDownloading ? 'not-allowed' : 'pointer',
              opacity: isDownloading ? 0.7 : 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start'
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                backgroundColor: '#fff',
                color: fileMeta.color,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '20px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                marginBottom: '8px'
              }}
            >
              {isDownloading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className={`fa-solid ${fileMeta.icon}`} />
              )}
            </div>
            <div
              title={fileName}
              style={{
                fontSize: '11px',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                color: '#212529',
                width: '100%'
              }}
            >
              {fileName.length > 22 ? `${fileName.slice(0, 19)}...` : fileName}
            </div>
          </button>
        )
      })}
    </div>
  )
}

function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

/** Prefer API quote/email link; otherwise build local supplier portal URL (same pattern as RfqView). */
function getSupplierEmailLink(lrfqId, supplier) {
  if (supplier?.quoteLink) return supplier.quoteLink
  if (supplier?.emailLink) return supplier.emailLink
  const mail = String(supplier?.supplierMailId || '').trim()
  if (!lrfqId || !mail) return ''
  const supplierId = String(
    supplier?.supplierId || supplier?._id || supplier?.id || ''
  ).trim()
  const base = `${window.location.origin}/lrfqSupplierView/${lrfqId}/${encodeURIComponent(mail)}`
  return supplierId ? `${base}/${encodeURIComponent(supplierId)}` : base
}

function applyResendEmailToLrfq(prevRfq, responseData, supplierEmail) {
  if (!prevRfq) return prevRfq

  const data = responseData?.data
  const quoteLink = data?.result?.quoteLink || ''
  const mailKey = supplierEmail.trim().toLowerCase()

  const nextSuppliers = Array.isArray(prevRfq.suppliers)
    ? prevRfq.suppliers.map((supplier) => {
        if (String(supplier.supplierMailId || '').trim().toLowerCase() !== mailKey) {
          return supplier
        }
        return {
          ...supplier,
          ...(data?.supplierName ? { supplierName: data.supplierName } : {}),
          ...(quoteLink ? { quoteLink, emailLink: quoteLink } : {})
        }
      })
    : prevRfq.suppliers

  return {
    ...prevRfq,
    isMailSent: true,
    ...(data?.pdfLink ? { pdfLink: data.pdfLink } : {}),
    ...(data?.pdfDownloadUrl ? { pdfDownloadUrl: data.pdfDownloadUrl } : {}),
    ...(data?.downloadUrl && !data?.pdfDownloadUrl
      ? { pdfDownloadUrl: data.downloadUrl, downloadUrl: data.downloadUrl }
      : {}),
    ...(Array.isArray(nextSuppliers) ? { suppliers: nextSuppliers } : {})
  }
}

function supplierHasSubmittedQuote(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.hasQuoteSubmitted === true) return true
  if (supplier.quoteSubmittedAt) return true
  if (Array.isArray(supplier.materialQuotes) && supplier.materialQuotes.length > 0) {
    return true
  }
  if (Array.isArray(supplier.terms) && supplier.terms.length > 0) return true
  return false
}

function supplierHasQuotedPrice(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.quoteSubmittedAt) return true
  if (supplier.hasQuoteSubmitted === true) return true
  const quotes = Array.isArray(supplier.materialQuotes)
    ? supplier.materialQuotes
    : []
  return quotes.some((quote) => {
    const quoted = Number(quote?.quotedPrice)
    const basic = Number(quote?.basicPrice)
    const total = Number(quote?.totalValue)
    if (Number.isFinite(quoted) && quoted > 0) return true
    if (Number.isFinite(basic) && basic > 0) return true
    if (Number.isFinite(total) && total > 0) return true
    const boq = Array.isArray(quote?.boq) ? quote.boq : []
    return boq.some((item) => {
      const qp = Number(item?.quotedPrice)
      const bp = Number(item?.basicPrice)
      return (
        (Number.isFinite(qp) && qp > 0) || (Number.isFinite(bp) && bp > 0)
      )
    })
  })
}

/** True when at least one supplier has quoted a price (Price Matching can start). */
function rfqHasAnySupplierQuote(rfq, suppliersList = []) {
  if (Array.isArray(suppliersList) && suppliersList.some(supplierHasQuotedPrice)) {
    return true
  }
  const history = getTargetPriceQuoteHistory(rfq)
  if (
    history.beforeTargetPrice.length > 0 ||
    history.afterTargetPrice.length > 0
  ) {
    return true
  }
  return false
}

function supplierHasOpenedLink(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (
    supplier.hasLinkOpened === true ||
    supplier.isLinkOpened === true ||
    supplier.linkOpened === true ||
    supplier.hasOpenedLink === true
  ) {
    return true
  }
  if (supplier.linkOpenedAt || supplier.linkVisitedAt || supplier.openedAt) return true
  return false
}

function getSupplierViewButtonProps(supplier) {
  if (supplierHasSubmittedQuote(supplier)) {
    return {
      className: 'btn btn-sm btn-blue',
      title: 'Quote submitted — view details'
    }
  }
  if (supplierHasOpenedLink(supplier)) {
    return {
      className: 'btn btn-sm btn-warning',
      title: 'Link opened — quote not submitted yet'
    }
  }
  return {
    className: 'btn btn-sm btn-secondary',
    title: 'Link not opened yet'
  }
}

function hasSupplierQuotes(supplier) {
  return supplierHasSubmittedQuote(supplier)
}

function getSupplierTerms(supplier) {
  return Array.isArray(supplier?.terms) ? supplier.terms : []
}

/** Normalize get-lrfq-by-id target price quote history payload. */
function getTargetPriceQuoteHistory(rfq) {
  if (!rfq || typeof rfq !== 'object') {
    return {
      targetPrice: null,
      beforeTargetPrice: [],
      afterTargetPrice: [],
      suppliersWithBeforeCount: 0,
      suppliersWithAfterCount: 0
    }
  }

  const bundle =
    rfq.targetPriceQuotes && typeof rfq.targetPriceQuotes === 'object'
      ? rfq.targetPriceQuotes
      : rfq

  const before = Array.isArray(bundle.beforeTargetPrice)
    ? bundle.beforeTargetPrice
    : Array.isArray(rfq.beforeTargetPrice)
      ? rfq.beforeTargetPrice
      : []
  const after = Array.isArray(bundle.afterTargetPrice)
    ? bundle.afterTargetPrice
    : Array.isArray(rfq.afterTargetPrice)
      ? rfq.afterTargetPrice
      : []

  const targetPrice = Array.isArray(bundle.targetPrice)
    ? bundle.targetPrice
    : Array.isArray(rfq.targetPrice)
      ? rfq.targetPrice
      : null

  return {
    targetPrice,
    beforeTargetPrice: before,
    afterTargetPrice: after,
    suppliersWithBeforeCount:
      Number(bundle.suppliersWithBeforeCount) || before.length,
    suppliersWithAfterCount:
      Number(bundle.suppliersWithAfterCount) || after.length
  }
}

function getLineQuotedPrice(line) {
  if (!line || typeof line !== 'object') return NaN
  const quoted = Number(line.quotedPrice)
  if (Number.isFinite(quoted)) return quoted
  const basic = Number(line.basicPrice)
  return Number.isFinite(basic) ? basic : NaN
}

function getLineEffectiveTargetPrice(line) {
  if (!line || typeof line !== 'object') return NaN
  const candidates = [
    line.targetPrice,
    line.supplierTargetPrice,
    line.allSuppliersTargetPrice
  ]
  for (const value of candidates) {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
  }
  return NaN
}

function getLinePriceDifference(line) {
  if (!line || typeof line !== 'object') return NaN
  const direct = Number(line.difference)
  if (Number.isFinite(direct)) return direct
  const quoted = getLineQuotedPrice(line)
  const target = getLineEffectiveTargetPrice(line)
  if (!Number.isFinite(quoted) || !Number.isFinite(target)) return NaN
  return quoted - target
}

function lineNeedsPriceMatching(line) {
  if (!line || typeof line !== 'object') return false
  if (line.needsPriceMatching === true) return true
  if (line.needsPriceMatching === false) return false
  const diff = getLinePriceDifference(line)
  return Number.isFinite(diff) && diff > 0
}

function supplierNeedsPriceMatching(supplier) {
  if (!supplier || typeof supplier !== 'object') return false
  if (supplier.needsPriceMatching === true) return true
  if (supplier.needsPriceMatching === false) return false
  const quotes = Array.isArray(supplier.materialQuotes)
    ? supplier.materialQuotes
    : []
  return quotes.some((quote) => {
    if (lineNeedsPriceMatching(quote)) return true
    const boqLines = Array.isArray(quote?.boq) ? quote.boq : []
    return boqLines.some(lineNeedsPriceMatching)
  })
}

function getBoqLineSupplierEntries(line) {
  if (!line || typeof line !== 'object') return []
  if (Array.isArray(line.suppliers)) return line.suppliers
  if (Array.isArray(line.quotes)) return line.quotes
  if (Array.isArray(line.supplierQuotes)) return line.supplierQuotes
  return []
}

function findBoqQuotedInHistoryEntry(entry, boqNo, boqId) {
  if (!entry) return NaN
  const finalRev = getFinalRevisionFromEntry(entry)
  const quote = pickHistoryQuoteObject(entry, finalRev)
  const quotes = Array.isArray(quote.materialQuotes) ? quote.materialQuotes : []
  for (const mq of quotes) {
    for (const item of getQuoteLines(mq)) {
      const itemId = getBoqLineId(item)
      const itemNo = getBoqLineNo(item)
      if (
        (boqId && itemId && String(itemId) === String(boqId)) ||
        (boqNo && itemNo && itemNo === String(boqNo).trim())
      ) {
        return getLineQuotedPrice(item)
      }
    }
  }
  return NaN
}

function getBeforeQuotedForSupplierBoq(rfq, supplierIdentity, boqNo, boqId) {
  const history = getTargetPriceQuoteHistory(rfq)
  const beforeEntry =
    (Array.isArray(history.beforeTargetPrice) ? history.beforeTargetPrice : []).find(
      (entry) => suppliersMatchIdentity(entry, supplierIdentity)
    ) || null
  if (!beforeEntry) return NaN
  return findBoqQuotedInHistoryEntry(beforeEntry, boqNo, boqId)
}

/** Normalize get-lrfq-by-id boqPriceMatching.boqLines payload. */
function getBoqPriceMatchingBundle(rfq) {
  if (!rfq || typeof rfq !== 'object') {
    return { boqLines: [] }
  }
  const bundle =
    rfq.boqPriceMatching && typeof rfq.boqPriceMatching === 'object'
      ? rfq.boqPriceMatching
      : {}
  const lines = Array.isArray(bundle.boqLines)
    ? bundle.boqLines
    : Array.isArray(rfq.boqPriceMatchingLines)
      ? rfq.boqPriceMatchingLines
      : []
  return { ...bundle, boqLines: lines }
}

/**
 * Find a quoted BOQ/lane row across parent materialQuotes.
 * Returns { item, parentQuote } — GST usually lives on parentQuote, not the lane.
 */
function findQuotedBoqMatch(materialQuotes, boqId, boqNo) {
  const quotes = Array.isArray(materialQuotes) ? materialQuotes : []
  const id = String(boqId || '').trim()
  const no = String(boqNo || '').trim()
  let matchedByNo = null
  for (const quote of quotes) {
    const lines = getQuoteLines(quote)
    for (const item of lines) {
      const itemId = getBoqLineId(item)
      const itemNo = getBoqLineNo(item)
      if (id && itemId && itemId === id) {
        return { item, parentQuote: quote }
      }
      if (!matchedByNo && no && itemNo && itemNo === no) {
        matchedByNo = { item, parentQuote: quote }
      }
    }
  }
  return matchedByNo
}

function findQuotedBoqItem(materialQuotes, boqId, boqNo) {
  return findQuotedBoqMatch(materialQuotes, boqId, boqNo)?.item || null
}

/**
 * BOQ lanes often only store basicPrice; gstPercent/gstValue are on the parent
 * material quote. Fill display fields from lane, then parent, then compute.
 */
function resolveQuotedLinePriceFields(item, parentQuote) {
  const basicPrice = Number(item?.basicPrice)
  const qty = Number(item?.quantity)
  const lineQty = Number.isFinite(qty) && qty > 0 ? qty : 1
  const lineBasic = Number.isFinite(basicPrice) ? basicPrice * lineQty : NaN

  const hasOwnGst =
    item?.gstPercent != null &&
    item?.gstPercent !== '' &&
    Number.isFinite(Number(item.gstPercent))
  const parentGst = Number(parentQuote?.gstPercent)
  const gstPercent = hasOwnGst
    ? Number(item.gstPercent)
    : Number.isFinite(parentGst)
      ? parentGst
      : NaN

  let gstValue = Number(item?.gstValue)
  if (
    !Number.isFinite(gstValue) &&
    Number.isFinite(lineBasic) &&
    Number.isFinite(gstPercent)
  ) {
    gstValue = (lineBasic * gstPercent) / 100
  } else if (
    Number.isFinite(gstValue) &&
    gstValue === 0 &&
    Number.isFinite(gstPercent) &&
    gstPercent > 0 &&
    Number.isFinite(lineBasic)
  ) {
    // Lane stored 0 while parent has GST — compute for display.
    gstValue = (lineBasic * gstPercent) / 100
  }

  let totalValue = Number(item?.totalValue)
  if (!Number.isFinite(totalValue) && Number.isFinite(lineBasic)) {
    totalValue =
      lineBasic + (Number.isFinite(gstValue) ? gstValue : 0)
  } else if (
    Number.isFinite(totalValue) &&
    Number.isFinite(lineBasic) &&
    Number.isFinite(gstPercent) &&
    gstPercent > 0 &&
    Number.isFinite(gstValue) &&
    gstValue > 0 &&
    Math.abs(totalValue - lineBasic) < 0.001
  ) {
    // totalValue was qty*basic only; include GST for display.
    totalValue = lineBasic + gstValue
  }

  return {
    basicPrice: Number.isFinite(basicPrice) ? basicPrice : item?.basicPrice,
    quantity: Number.isFinite(qty) ? qty : item?.quantity,
    gstPercent: Number.isFinite(gstPercent) ? gstPercent : item?.gstPercent,
    gstValue: Number.isFinite(gstValue) ? gstValue : item?.gstValue,
    totalValue: Number.isFinite(totalValue) ? totalValue : item?.totalValue
  }
}

/**
 * Build per-BOQ supplier quoted-vs-target rows from every parent material and
 * nested BOQ lane (full tree), matched to supplier materialQuotes.
 */
function buildBoqPriceMatchingLinesFromSuppliers(rfq, suppliers = []) {
  const materials = Array.isArray(rfq?.materials) ? rfq.materials : []
  const quotedSuppliers = (Array.isArray(suppliers) ? suppliers : []).filter(
    supplierHasQuotedPrice
  )
  if (materials.length === 0) return []

  const targetMap = getLrfqTargetMap(rfq)
  const lines = []
  materials.forEach((material, matIndex) => {
    const lineNo = matIndex + 1
    const materialLanes = getMaterialBoqLines(material)
    const boqLines = materialLanes.length > 0 ? materialLanes : [material]
    const parentLabel = String(
      material.sourceDescription || material.sourceCode || ''
    ).trim()
    const parentDescription =
      parentLabel && parentLabel !== '-'
        ? parentLabel
        : `Line ${lineNo}`

    boqLines.forEach((boqItem, boqIndex) => {
      const boqId = getBoqLineId(boqItem)
      const boqNo =
        materialLanes.length > 0
          ? getBoqLineNo(boqItem, `${lineNo}.${boqIndex + 1}`)
          : String(lineNo)
      const savedTarget = boqId ? targetMap[boqId] : undefined
      const rfqTarget = Number.isFinite(savedTarget)
        ? savedTarget
        : getLineEffectiveTargetPrice(boqItem)
      const supplierEntries = []

      quotedSuppliers.forEach((supplier) => {
        const match = findQuotedBoqMatch(
          supplier.materialQuotes,
          boqId,
          boqNo
        )
        const matched = match?.item
        if (!matched) return
        const priceFields = resolveQuotedLinePriceFields(
          matched,
          match.parentQuote
        )
        const quotedPrice = getLineQuotedPrice({
          ...matched,
          basicPrice: priceFields.basicPrice
        })
        const targetPrice = getLineEffectiveTargetPrice(matched)
        const effectiveTarget = Number.isFinite(targetPrice)
          ? targetPrice
          : rfqTarget
        const difference = Number.isFinite(Number(matched.difference))
          ? Number(matched.difference)
          : Number.isFinite(quotedPrice) && Number.isFinite(effectiveTarget)
            ? quotedPrice - effectiveTarget
            : NaN
        const beforeQuotedPrice = getBeforeQuotedForSupplierBoq(
          rfq,
          supplier,
          boqNo,
          boqId
        )
        supplierEntries.push({
          supplierId: supplier.supplierId,
          supplierName: supplier.supplierName,
          supplierMailId: supplier.supplierMailId,
          quotedPrice,
          beforeQuotedPrice: Number.isFinite(beforeQuotedPrice)
            ? beforeQuotedPrice
            : undefined,
          basicPrice: priceFields.basicPrice,
          gstPercent: priceFields.gstPercent,
          gstValue: priceFields.gstValue,
          totalValue: priceFields.totalValue,
          quantity: priceFields.quantity,
          comments: matched.comments || matched.technicalResponse || '',
          targetPrice: effectiveTarget,
          allSuppliersTargetPrice: matched.allSuppliersTargetPrice,
          supplierTargetPrice: matched.supplierTargetPrice,
          difference,
          needsPriceMatching:
            matched.needsPriceMatching === true ||
            matched.needsPriceMatching === false
              ? matched.needsPriceMatching
              : Number.isFinite(difference) && difference > 0
        })
      })

      // Keep every parent/child BOQ row even when a supplier has not quoted it yet.
      lines.push({
        boq_id: boqId,
        boqNo,
        isBoqChild: materialLanes.length > 0,
        materialDescription:
          materialLanes.length > 0
            ? getLaneLabel(boqItem)
            : material.sourceDescription || material.sourceCode || '-',
        parentDescription:
          materialLanes.length > 0 ? parentDescription : '',
        parentLineNo: lineNo,
        parentSourceCode: material.sourceCode || '',
        parentSourceDescription:
          material.sourceDescription || material.sourceCode || '-',
        parentHsn: material.hsn || material.hsnCode || '',
        parentUom: material.uom || '',
        parentQuantity: material.quantity,
        quantity:
          materialLanes.length > 0
            ? boqItem.quantity ?? material.quantity
            : material.quantity,
        uom: material.uom || '',
        hsn: material.hsn || material.hsnCode || '',
        sourceCode: materialLanes.length > 0 ? '' : material.sourceCode || '',
        targetPrice: Number.isFinite(rfqTarget)
          ? rfqTarget
          : supplierEntries.find((s) =>
              Number.isFinite(Number(s.targetPrice))
            )?.targetPrice,
        suppliers: supplierEntries,
        needsPriceMatching: supplierEntries.some(lineNeedsPriceMatching)
      })
    })
  })
  return lines
}

/**
 * Parent material quote holds GST % / GST value / totals (BOQ lanes usually only have basicPrice).
 */
function findParentMaterialQuote(suppliers, material, parentNo) {
  const matId = getBoqLineId(material)
  const index = Number(parentNo) - 1
  const list = (Array.isArray(suppliers) ? suppliers : []).filter(
    supplierHasQuotedPrice
  )
  for (const supplier of list) {
    const quotes = Array.isArray(supplier.materialQuotes)
      ? supplier.materialQuotes
      : []
    let quote = null
    if (matId) {
      quote = quotes.find((q) => getBoqLineId(q) === matId) || null
    }
    if (!quote && Number.isFinite(index) && index >= 0 && quotes[index]) {
      quote = quotes[index]
    }
    if (quote) return { quote, supplier }
  }
  return null
}

/** Group flat BOQ matching lines into parent material + child lane groups (view-page shape). */
function groupBoqPriceMatchingLinesByParent(lines, materials = [], suppliers = []) {
  const mats = Array.isArray(materials) ? materials : []
  const order = []
  const byParent = new Map()

  ;(Array.isArray(lines) ? lines : []).forEach((line) => {
    const parentNo = String(
      line?.parentLineNo ||
        String(line?.boqNo || '')
          .split('.')[0]
          .trim() ||
        '0'
    )
    if (!byParent.has(parentNo)) {
      const matIndex = Number(parentNo) - 1
      const material =
        Number.isFinite(matIndex) && matIndex >= 0 ? mats[matIndex] : null
      const parentMatch = findParentMaterialQuote(suppliers, material, parentNo)
      const parentQuote = parentMatch?.quote || null
      const group = {
        parentNo,
        material,
        sourceCode: line.parentSourceCode || material?.sourceCode || '',
        sourceDescription:
          line.parentSourceDescription ||
          material?.sourceDescription ||
          material?.sourceCode ||
          `Line ${parentNo}`,
        hsn: line.parentHsn || material?.hsn || material?.hsnCode || '',
        uom: line.parentUom || material?.uom || '',
        quantity:
          line.parentQuantity != null
            ? line.parentQuantity
            : material?.quantity,
        // GST / totals belong on the parent material quote.
        basicPrice: parentQuote?.basicPrice,
        gstPercent: parentQuote?.gstPercent,
        gstValue: parentQuote?.gstValue,
        totalValue: parentQuote?.totalValue,
        supplier: parentMatch?.supplier || null,
        supplierName:
          parentMatch?.supplier?.supplierName ||
          parentMatch?.supplier?.supplierMailId ||
          '',
        hasBoqChildren: false,
        lines: []
      }
      byParent.set(parentNo, group)
      order.push(group)
    }
    const group = byParent.get(parentNo)
    group.lines.push(line)
    if (line.isBoqChild) group.hasBoqChildren = true
  })

  return order
}

/**
 * Prefer the full materials/BOQ tree from the LRFQ. Incomplete API
 * boqPriceMatching.boqLines must not hide parent lines 2.x / 3.x etc.
 */
function getResolvedBoqPriceMatchingLines(rfq, suppliers = []) {
  const finalSuppliers = getSuppliersForFinalBoqComparison(rfq, suppliers)
  const built = buildBoqPriceMatchingLinesFromSuppliers(rfq, finalSuppliers)
  const fromApi = getBoqPriceMatchingBundle(rfq).boqLines
  if (built.length === 0) return fromApi
  if (fromApi.length === 0) return built

  const apiByKey = new Map()
  fromApi.forEach((line) => {
    const id = String(line?.boq_id || line?.uniqueId || '').trim()
    const no = String(line?.boqNo || line?.sNo || '').trim()
    if (id) apiByKey.set(`id:${id}`, line)
    if (no) apiByKey.set(`no:${no}`, line)
  })

  const merged = built.map((line) => {
    const id = String(line?.boq_id || '').trim()
    const no = String(line?.boqNo || '').trim()
    const apiLine =
      (id && apiByKey.get(`id:${id}`)) || (no && apiByKey.get(`no:${no}`)) || null
    if (!apiLine) return line
    const apiSuppliers = getBoqLineSupplierEntries(apiLine)
    return {
      ...apiLine,
      ...line,
      suppliers:
        Array.isArray(line.suppliers) && line.suppliers.length > 0
          ? line.suppliers
          : apiSuppliers,
      targetPrice:
        line.targetPrice != null && line.targetPrice !== ''
          ? line.targetPrice
          : apiLine.targetPrice,
      needsPriceMatching:
        line.needsPriceMatching === true || line.needsPriceMatching === false
          ? line.needsPriceMatching
          : apiLine.needsPriceMatching
    }
  })

  fromApi.forEach((apiLine) => {
    const id = String(apiLine?.boq_id || apiLine?.uniqueId || '').trim()
    const no = String(apiLine?.boqNo || apiLine?.sNo || '').trim()
    const exists = merged.some(
      (line) =>
        (id && String(line.boq_id || '') === id) ||
        (no && String(line.boqNo || '') === no)
    )
    if (!exists) merged.push(apiLine)
  })
  return merged
}

function normalizePriceMatchingSupplierEntry(entry) {
  if (!entry) return null
  if (typeof entry === 'string') {
    const value = String(entry).trim()
    if (!value) return null
    return {
      supplierId: value,
      supplierMailId: value.includes('@') ? value : '',
      supplierName: value
    }
  }
  if (typeof entry !== 'object') return null
  return entry
}

function suppliersMatchIdentity(a, b) {
  if (!a || !b) return false
  if (a.supplierId && b.supplierId && String(a.supplierId) === String(b.supplierId)) {
    return true
  }
  const mailA = String(a.supplierMailId || '')
    .trim()
    .toLowerCase()
  const mailB = String(b.supplierMailId || '')
    .trim()
    .toLowerCase()
  return Boolean(mailA && mailB && mailA === mailB)
}

/** Suppliers who still need price matching (API list, else derived). */
function getPriceMatchingSuppliersList(rfq, allSuppliers = []) {
  const suppliers = Array.isArray(allSuppliers) ? allSuppliers : []
  const raw = Array.isArray(rfq?.priceMatchingSuppliers)
    ? rfq.priceMatchingSuppliers
    : []
  if (raw.length > 0) {
    return raw
      .map(normalizePriceMatchingSupplierEntry)
      .filter(Boolean)
      .map((entry) => {
        const match = suppliers.find((s) => suppliersMatchIdentity(s, entry))
        return match
          ? { ...match, ...entry, needsPriceMatching: true }
          : { ...entry, needsPriceMatching: true }
      })
  }
  return suppliers.filter(supplierNeedsPriceMatching)
}

/** True when any material / BOQ lane target price has been set (> 0). */
function rfqHasSavedPriceMatchingTargets(rfq, suppliers = []) {
  if (!rfq || typeof rfq !== 'object') return false
  if (Object.values(getLrfqTargetMap(rfq)).some((price) => price > 0)) {
    return true
  }

  const materials = Array.isArray(rfq.materials) ? rfq.materials : []
  for (const material of materials) {
    if (getLineEffectiveTargetPrice(material) > 0) return true
    if (
      getMaterialBoqLines(material).some(
        (item) => getLineEffectiveTargetPrice(item) > 0
      )
    ) {
      return true
    }
  }

  const apiLines = getBoqPriceMatchingBundle(rfq).boqLines
  for (const line of apiLines) {
    if (getLineEffectiveTargetPrice(line) > 0) return true
    if (
      getBoqLineSupplierEntries(line).some(
        (entry) => getLineEffectiveTargetPrice(entry) > 0
      )
    ) {
      return true
    }
  }

  return (Array.isArray(suppliers) ? suppliers : []).some((supplier) => {
    const quotes = Array.isArray(supplier?.materialQuotes)
      ? supplier.materialQuotes
      : []
    return quotes.some((quote) => {
      if (getLineEffectiveTargetPrice(quote) > 0) return true
      return (Array.isArray(quote?.boq) ? quote.boq : []).some(
        (item) => getLineEffectiveTargetPrice(item) > 0
      )
    })
  })
}

function pickHistoryQuoteObject(entry, quoteOverride = null) {
  const candidates = [
    quoteOverride,
    quoteOverride?.quote,
    entry?.quote,
    entry
  ].filter((item) => item && typeof item === 'object')

  for (const candidate of candidates) {
    if (Array.isArray(candidate.materialQuotes)) return candidate
  }
  return candidates[0] || {}
}

function getEntryRevisions(entry) {
  if (Array.isArray(entry?.allRevisions) && entry.allRevisions.length > 0) {
    return entry.allRevisions
  }
  if (entry?.quote && typeof entry.quote === 'object') return [entry.quote]
  return []
}

/** Latest / final revision for a before or after history entry. */
function getFinalRevisionFromEntry(entry) {
  const revisions = getEntryRevisions(entry)
  if (revisions.length === 0) {
    if (entry?.quote && typeof entry.quote === 'object') return entry.quote
    return entry && typeof entry === 'object' ? entry : null
  }
  return revisions.reduce((best, rev) => {
    const bestNo = Number(best?.revisionNo)
    const revNo = Number(rev?.revisionNo)
    if (Number.isFinite(revNo) && Number.isFinite(bestNo)) {
      return revNo >= bestNo ? rev : best
    }
    if (Number.isFinite(revNo) && !Number.isFinite(bestNo)) return rev
    return best
  }, revisions[revisions.length - 1])
}

/**
 * Prefer each supplier's final after-matching revision materialQuotes for BOQ
 * comparison; fall back to live supplier quotes (already the current final).
 */
function getSuppliersForFinalBoqComparison(rfq, suppliers = []) {
  const history = getTargetPriceQuoteHistory(rfq)
  const after = Array.isArray(history.afterTargetPrice)
    ? history.afterTargetPrice
    : []
  return (Array.isArray(suppliers) ? suppliers : []).map((supplier) => {
    const afterEntry =
      after.find((entry) => suppliersMatchIdentity(entry, supplier)) || null
    if (!afterEntry) return supplier
    const finalRev = getFinalRevisionFromEntry(afterEntry)
    const quote = pickHistoryQuoteObject(afterEntry, finalRev)
    if (!Array.isArray(quote.materialQuotes) || quote.materialQuotes.length === 0) {
      return supplier
    }
    // Do not replace live quotes with a thinner history snapshot (would drop parents 2/3…).
    const liveCount = Array.isArray(supplier.materialQuotes)
      ? supplier.materialQuotes.length
      : 0
    if (quote.materialQuotes.length < liveCount) return supplier
    return {
      ...supplier,
      materialQuotes: quote.materialQuotes,
      grossTotalBasicPrice:
        quote.grossTotalBasicPrice ?? supplier.grossTotalBasicPrice,
      grossGstValue: quote.grossGstValue ?? supplier.grossGstValue,
      grossTotalValue: quote.grossTotalValue ?? supplier.grossTotalValue,
      quoteSubmittedAt:
        quote.quoteSubmittedAt || supplier.quoteSubmittedAt,
      _revisionNo: finalRev?.revisionNo ?? quote?.revisionNo ?? null
    }
  })
}

function formatSignedAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  const abs = Math.abs(num).toFixed(2)
  if (num > 0) return `+${abs}`
  if (num < 0) return `-${abs}`
  return abs
}

/** Parent-only label + amount (headers stay unchanged). */
function renderParentLabeledAmountCell(
  label,
  value,
  className = 'text-right',
  key,
  title
) {
  const hasValue =
    value != null && value !== '' && Number.isFinite(Number(value))
  return (
    <td
      key={key}
      className={`${className} align-middle`}
      style={{ whiteSpace: 'nowrap' }}
      title={title || undefined}
    >
      <div
        className="text-muted"
        style={{ fontSize: '10px', fontWeight: 600, lineHeight: 1.2 }}
      >
        {label}
      </div>
      <div className="fwt_600" style={{ fontSize: '13px' }}>
        {hasValue ? formatAmount(value) : '—'}
      </div>
    </td>
  )
}

function renderParentTotalBasicCell(
  value,
  className = 'text-right',
  key,
  title
) {
  return renderParentLabeledAmountCell(
    'Total Basic',
    value,
    className,
    key,
    title
  )
}

function renderParentTotalTargetCell(
  value,
  className = 'text-right',
  key,
  title
) {
  return renderParentLabeledAmountCell(
    'Total Target',
    value,
    `${className} text-primary`,
    key,
    title
  )
}

/** Format a number for formula tooltips (trim trailing zeros). */
function formatCalcNumber(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return String(value ?? '')
  if (Number.isInteger(num)) return String(num)
  return String(parseFloat(num.toFixed(4)))
}

function ordinalN(n) {
  const v = Number(n)
  if (!Number.isFinite(v) || v < 1) return String(n)
  const mod100 = v % 100
  if (mod100 >= 11 && mod100 <= 13) return `${v}th`
  switch (v % 10) {
    case 1:
      return `${v}st`
    case 2:
      return `${v}nd`
    case 3:
      return `${v}rd`
    default:
      return `${v}th`
  }
}

function getQuotedPriceFromMaterialQuotes(materialQuotes, boqId, boqNo) {
  const matched = findQuotedBoqItem(materialQuotes, boqId, boqNo)
  return matched ? getLineQuotedPrice(matched) : NaN
}

/** Sorted target prices for a BOQ lane from targetPrice[].history (includes current). */
function getLrfqBoqTargetHistoryPrices(rfq, boqId) {
  if (!boqId || !Array.isArray(rfq?.targetPrice)) return []
  const item = rfq.targetPrice.find(
    (t) => String(t?.uniqueId || '').trim() === String(boqId).trim()
  )
  if (!item) return []
  const history = Array.isArray(item.history) ? [...item.history] : []
  history.sort((a, b) => {
    const ta = new Date(a?.updatedAt || 0).getTime()
    const tb = new Date(b?.updatedAt || 0).getTime()
    return ta - tb
  })
  if (history.length > 0) {
    return history
      .map((h) => Number(h?.targetPrice))
      .filter((n) => Number.isFinite(n))
  }
  const current = Number(item.targetPrice)
  return Number.isFinite(current) ? [current] : []
}

/** Qty used when extending unit target → line/parent total (Excel: qty × target). */
function getBoqLineQuantityForTotal(line) {
  const candidates = [
    line?.quantity,
    line?.qty,
    line?.boqQuantity,
    line?.parentQuantity
  ]
  for (const value of candidates) {
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
  }
  return 1
}

/**
 * Prefer API-provided parent Total Target (matches export Excel parent cell).
 * Falls back to NaN so caller can compute sum(qty × unit target).
 */
function getApiParentTargetTotal(rfq, group, targetIndex) {
  const material = group?.material
  const pickFinite = (...vals) => {
    for (const value of vals) {
      const num = Number(value)
      if (Number.isFinite(num) && num >= 0) return num
    }
    return NaN
  }

  if (material && typeof material === 'object') {
    const fromMaterial = pickFinite(
      material.totalTargetPrice,
      material.totalTarget,
      material.targetPriceTotal,
      material.parentTotalTargetPrice,
      material.totalTargetBasicPrice
    )
    if (Number.isFinite(fromMaterial) && targetIndex === 0) return fromMaterial

    const matHistory = Array.isArray(material.targetPriceHistory)
      ? material.targetPriceHistory
      : Array.isArray(material.targetHistory)
        ? material.targetHistory
        : []
    if (matHistory[targetIndex]) {
      const fromHist = pickFinite(
        matHistory[targetIndex].totalTargetPrice,
        matHistory[targetIndex].totalTarget,
        matHistory[targetIndex].targetPriceTotal,
        matHistory[targetIndex].totalTargetBasicPrice
      )
      if (Number.isFinite(fromHist)) return fromHist
    }
  }

  const parentId = getBoqLineId(material)
  if (parentId && Array.isArray(rfq?.targetPrice)) {
    const parentTarget = rfq.targetPrice.find(
      (t) => String(t?.uniqueId || '').trim() === parentId
    )
    if (parentTarget) {
      const history = Array.isArray(parentTarget.history)
        ? [...parentTarget.history].sort((a, b) => {
            const ta = new Date(a?.updatedAt || 0).getTime()
            const tb = new Date(b?.updatedAt || 0).getTime()
            return ta - tb
          })
        : []
      const histEntry = history[targetIndex]
      if (histEntry) {
        // Only explicit total fields — plain targetPrice on parent is usually unit.
        const fromParentHist = pickFinite(
          histEntry.totalTargetPrice,
          histEntry.totalTarget,
          histEntry.targetPriceTotal,
          histEntry.totalTargetBasicPrice
        )
        if (Number.isFinite(fromParentHist)) return fromParentHist
      }
      if (targetIndex === 0 || history.length === 0) {
        const fromParent = pickFinite(
          parentTarget.totalTargetPrice,
          parentTarget.totalTarget,
          parentTarget.targetPriceTotal,
          parentTarget.totalTargetBasicPrice
        )
        if (Number.isFinite(fromParent)) return fromParent
      }
    }
  }

  return NaN
}

/** True when a quoteRevision is the pre–price-matching (1st quoted) snapshot. */
function isBeforeTargetPriceRevision(rev) {
  if (!rev || typeof rev !== 'object') return false
  const phase = String(rev.phase || '')
    .trim()
    .toLowerCase()
  if (phase === 'before_target_price' || phase === 'before-target-price') {
    return true
  }
  if (phase === 'after_target_price' || phase === 'after-target-price') {
    return false
  }
  const revNo = Number(rev.revisionNo)
  if (Number.isFinite(revNo) && revNo === 0) return true
  const targetPriceId = String(rev.targetPriceId || '').trim()
  if (!targetPriceId && Number.isFinite(revNo) && revNo <= 0) return true
  return false
}

function sortQuoteRevisionsAsc(revisions) {
  return [...revisions].sort((a, b) => {
    const ra = Number(a?.revisionNo)
    const rb = Number(b?.revisionNo)
    if (Number.isFinite(ra) && Number.isFinite(rb) && ra !== rb) return ra - rb
    return (
      new Date(a?.createdAt || a?.updatedAt || 0).getTime() -
      new Date(b?.createdAt || b?.updatedAt || 0).getTime()
    )
  })
}

function getSupplierQuoteHistoryRounds(supplier) {
  const history = Array.isArray(supplier?.quoteHistory) ? supplier.quoteHistory : []
  const initial = history.find((h) => Number(h?.round) === 0) || null
  let maxRound = 0
  history.forEach((h) => {
    const r = Number(h?.round)
    if (Number.isFinite(r) && r > maxRound) maxRound = r
  })
  const revisions = Array.from({ length: maxRound }, () => null)
  history.forEach((h) => {
    const r = Number(h?.round)
    if (Number.isFinite(r) && r >= 1) revisions[r - 1] = h
  })
  return { hasHistory: history.length > 0, initial, revisions }
}

/** After-target quote revisions only (excludes phase: before_target_price). */
function getSupplierAfterTargetRevisions(supplier) {
  const fromHistory = getSupplierQuoteHistoryRounds(supplier)
  if (fromHistory.hasHistory) return fromHistory.revisions
  const revisions = Array.isArray(supplier?.quoteRevisions)
    ? supplier.quoteRevisions
    : []
  return sortQuoteRevisionsAsc(
    revisions.filter((rev) => !isBeforeTargetPriceRevision(rev))
  )
}

/** Sorted after-target revision quoted prices for a BOQ lane. */
function getSupplierBoqRevisionPrices(supplier, boqId, boqNo) {
  return getSupplierAfterTargetRevisions(supplier).map((rev) =>
    getQuotedPriceFromMaterialQuotes(rev?.materialQuotes, boqId, boqNo)
  )
}

/**
 * 1st Quoted Price = supplier quote before any target was set.
 * Prefer supplier.beforeTargetPrice, else quoteRevisions phase before_target_price.
 */
function getSupplierBoqInitialQuotedPrice(rfq, supplier, entry, line) {
  const boqNo = getBoqLineNo(line)
  const boqId = getBoqLineId(line) || line?.boq_id || line?.boqId

  const direct = Number(
    entry?.beforeQuotedPrice ??
      entry?.beforePrice ??
      entry?.quotedPriceBefore ??
      entry?.beforeTargetQuotedPrice
  )
  if (Number.isFinite(direct)) return direct

    const histInitial = getSupplierQuoteHistoryRounds(supplier).initial
  if (histInitial) {
    const fromHist = getQuotedPriceFromMaterialQuotes(
      histInitial.materialQuotes,
      boqId,
      boqNo
    )
    if (Number.isFinite(fromHist)) return fromHist
  }

  // Dedicated before-target snapshot on the supplier (preferred).
  const beforeBundle =
    supplier?.beforeTargetPrice && typeof supplier.beforeTargetPrice === 'object'
      ? supplier.beforeTargetPrice
      : null
  if (beforeBundle) {
    const fromBefore = getQuotedPriceFromMaterialQuotes(
      beforeBundle.materialQuotes,
      boqId,
      boqNo
    )
    if (Number.isFinite(fromBefore)) return fromBefore
  }

  // quoteRevisions entry with phase: before_target_price (revisionNo 0).
  const beforeRev = sortQuoteRevisionsAsc(
    (Array.isArray(supplier?.quoteRevisions) ? supplier.quoteRevisions : []).filter(
      isBeforeTargetPriceRevision
    )
  ).slice(-1)[0]
  if (beforeRev) {
    const fromRev = getQuotedPriceFromMaterialQuotes(
      beforeRev.materialQuotes,
      boqId,
      boqNo
    )
    if (Number.isFinite(fromRev)) return fromRev
  }

  const fromHistory = getBeforeQuotedForSupplierBoq(
    rfq,
    entry || supplier,
    boqNo,
    boqId
  )
  if (Number.isFinite(fromHistory)) return fromHistory

  // No after-target revisions yet — current materialQuotes is still the 1st quote.
   if (!getSupplierAfterTargetRevisions(supplier).some(Boolean)) {
    const fromCurrent = getQuotedPriceFromMaterialQuotes(
      supplier?.materialQuotes,
      boqId,
      boqNo
    )
    if (Number.isFinite(fromCurrent)) return fromCurrent
    return getLineQuotedPrice(entry)
  }
  return NaN
}

/**
 * Parent material total basic from a materialQuotes snapshot
 * (API basicPrice on the parent quote — not lane unit price).
 */
function getParentMaterialTotalBasic(materialQuotes, material, parentNo) {
  const matId = getBoqLineId(material)
  const index = Number(parentNo) - 1
  const quotes = Array.isArray(materialQuotes) ? materialQuotes : []
  let quote = null
  if (matId) {
    quote = quotes.find((q) => getBoqLineId(q) === matId) || null
  }
  if (!quote && Number.isFinite(index) && index >= 0) {
    quote = quotes[index] || null
  }
  const basic = Number(quote?.basicPrice)
  return Number.isFinite(basic) ? basic : NaN
}

/**
 * Parent Total Target for a history index = sum(qty × unit target).
 * Prefer API parent total only when lane-level sum cannot be computed.
 */
function getParentTargetBasicTotal(rfq, childLines, targetIndex, group = null) {
  let sum = 0
  let found = false
  ;(Array.isArray(childLines) ? childLines : []).forEach((line) => {
    const boqId = line?.boq_id || getBoqLineId(line)
    const targets = getLrfqBoqTargetHistoryPrices(rfq, boqId)
    let unit = Number(targets[targetIndex])
    if (!Number.isFinite(unit) && targetIndex === 0) {
      unit = getLineEffectiveTargetPrice(line)
    }
    if (!Number.isFinite(unit)) return
    const qty = getBoqLineQuantityForTotal(line)
    sum += unit * qty
    found = true
  })
  if (found) return sum
  return getApiParentTargetTotal(rfq, group, targetIndex)
}

/**
 * Parent material quantity (Excel L4). Used as:
 * Parent Total = parentQty × Σ(childQty × unitPrice)
 */
function getParentMaterialQuantity(group) {
  const qty = Number(
    group?.quantity ??
      group?.parentQuantity ??
      group?.material?.quantity
  )
  return Number.isFinite(qty) && qty > 0 ? qty : 1
}

/**
 * Parent-row history values (Excel formula):
 *   parentQty × Σ(childQty × unitPrice)
 * e.g. 2 × (150×5 + 150×10 + 150×15) = 2 × 4500 = 9000
 *      2 × (100×5 + 100×10 + 100×15) = 2 × 3000 = 6000
 * Also builds hover formula text per column.
 */
function getParentPriceMatchingHistoryValues(rfq, supplier, group, columnDefs) {
  const values = {}
  const formulas = {}
  const cols = Array.isArray(columnDefs) ? columnDefs : []
  cols.forEach((col) => {
    values[col.key] = NaN
    formulas[col.key] = ''
  })
  if (!supplier) return { values, formulas }

  const allLines = Array.isArray(group?.lines) ? group.lines : []
  const childLines = group?.hasBoqChildren
    ? allLines.filter((line) => line?.isBoqChild)
    : allLines
  const parentQty = getParentMaterialQuantity(group)

  const laneSums = {}
  const parts = {}
  const found = {}
  cols.forEach((col) => {
    laneSums[col.key] = 0
    parts[col.key] = []
    found[col.key] = false
  })

  childLines.forEach((line) => {
    const entry =
      getBoqLineSupplierEntries(line).find((e) =>
        suppliersMatchIdentity(e, supplier)
      ) || null
    if (!entry) return
    const childQty = getBoqLineQuantityForTotal(entry || line)
    if (!Number.isFinite(childQty) || childQty <= 0) return

    // Same unit values shown on the child row.
    const history = getLrfqPriceMatchingHistoryCellValues(
      rfq,
      supplier,
      entry,
      line,
      cols
    )
    cols.forEach((col) => {
      const unit = Number(history.values?.[col.key])
      if (!Number.isFinite(unit)) return
      laneSums[col.key] += unit * childQty
      parts[col.key].push(
        `${formatCalcNumber(unit)}×${formatCalcNumber(childQty)}`
      )
      found[col.key] = true
    })
  })

  const buildFormula = (laneSum, partList, total) => {
    if (!partList.length) return ''
    const inner = partList.join(' + ')
    return `${formatCalcNumber(parentQty)} × (${inner}) = ${formatCalcNumber(parentQty)} × ${formatCalcNumber(laneSum)} = ${formatCalcNumber(total)}`
  }

  cols.forEach((col) => {
    if (found[col.key]) {
      const total = laneSums[col.key] * parentQty
      values[col.key] = total
      formulas[col.key] = buildFormula(
        laneSums[col.key],
        parts[col.key],
        total
      )
      return
    }
    // Fallback for target cols when child history lacks a round.
    if (col.kind === 'target') {
      let laneSum = 0
      const fallbackParts = []
      let laneFound = false
      childLines.forEach((line) => {
        const boqId = line?.boq_id || getBoqLineId(line)
        const targets = getLrfqBoqTargetHistoryPrices(rfq, boqId)
        let unit = Number(targets[col.index])
        if (!Number.isFinite(unit) && col.index === 0) {
          unit = getLineEffectiveTargetPrice(line)
        }
        if (!Number.isFinite(unit)) return
        const childQty = getBoqLineQuantityForTotal(line)
        laneSum += unit * childQty
        fallbackParts.push(
          `${formatCalcNumber(unit)}×${formatCalcNumber(childQty)}`
        )
        laneFound = true
      })
      if (laneFound) {
        const total = laneSum * parentQty
        values[col.key] = total
        formulas[col.key] = buildFormula(laneSum, fallbackParts, total)
      } else {
        // API parent total is already Excel-extended — use as-is.
        values[col.key] = getApiParentTargetTotal(rfq, group, col.index)
      }
    }
  })
  return { values, formulas }
}

/**
 * Column defs: 1st Quoted → 1st Target → 1st Revision → … → Last Target → Last Revision.
 */
function buildLrfqPriceMatchingHistoryColumnDefs(rfq, lines, suppliers) {
  let maxTargets = 0
  let maxRevisions = 0
  const list = Array.isArray(lines) ? lines : []
  const supplierList = Array.isArray(suppliers) ? suppliers : []

  list.forEach((line) => {
    const boqId = line?.boq_id || getBoqLineId(line)
    maxTargets = Math.max(
      maxTargets,
      getLrfqBoqTargetHistoryPrices(rfq, boqId).length
    )
    getBoqLineSupplierEntries(line).forEach((entry) => {
      const supplier =
        supplierList.find((s) => suppliersMatchIdentity(s, entry)) || entry
      maxRevisions = Math.max(
        maxRevisions,
        getSupplierBoqRevisionPrices(supplier, boqId, getBoqLineNo(line)).length
      )
    })
  })

  if (Array.isArray(rfq?.targetPrice)) {
    rfq.targetPrice.forEach((t) => {
      const histLen = Array.isArray(t?.history) ? t.history.length : 0
      if (histLen > 0) maxTargets = Math.max(maxTargets, histLen)
      else if (Number.isFinite(Number(t?.targetPrice))) {
        maxTargets = Math.max(maxTargets, 1)
      }
    })
  }
  supplierList.forEach((s) => {
    maxRevisions = Math.max(
      maxRevisions,
      getSupplierAfterTargetRevisions(s).length
    )
  })

  const rounds = Math.max(maxTargets, maxRevisions)
  const cols = [
    { key: 'initialQuoted', label: 'Initial Quote Price', kind: 'initial' }
  ]
  for (let i = 0; i < rounds; i++) {
    const isLast = i === rounds - 1 && rounds > 1
    cols.push({
      key: `target-${i}`,
      label: isLast ? 'Last Target Price' : `${ordinalN(i + 1)} Target Price`,
      kind: 'target',
      index: i
    })
    cols.push({
      key: `revision-${i}`,
      label: isLast
        ? 'Last Counter-Offer Price'
        : `Counter-Offer${i + 1}`,
      kind: 'revision',
      index: i
    })
  }
  return cols
}

function getLrfqPriceMatchingHistoryCellValues(
  rfq,
  supplier,
  entry,
  line,
  columnDefs
) {
  const boqId = line?.boq_id || getBoqLineId(line)
  const boqNo = getBoqLineNo(line)
  const targets = getLrfqBoqTargetHistoryPrices(rfq, boqId)
  const revisions = getSupplierBoqRevisionPrices(supplier, boqId, boqNo)
  const initial = getSupplierBoqInitialQuotedPrice(rfq, supplier, entry, line)

  const values = {}
  ;(Array.isArray(columnDefs) ? columnDefs : []).forEach((col) => {
    if (col.kind === 'initial') values[col.key] = initial
    else if (col.kind === 'target') values[col.key] = targets[col.index]
    else if (col.kind === 'revision') values[col.key] = revisions[col.index]
  })

  const lastFiniteTarget = [...targets].reverse().find(Number.isFinite)
  const lastTarget = Number.isFinite(lastFiniteTarget)
    ? lastFiniteTarget
    : getLineEffectiveTargetPrice(entry) || getLineEffectiveTargetPrice(line)

  let lastQuoted = [...revisions].reverse().find(Number.isFinite)
  if (!Number.isFinite(lastQuoted)) lastQuoted = initial
  if (!Number.isFinite(lastQuoted)) lastQuoted = getLineQuotedPrice(entry)
  if (!Number.isFinite(lastQuoted)) {
    lastQuoted = getQuotedPriceFromMaterialQuotes(
      supplier?.materialQuotes,
      boqId,
      boqNo
    )
  }
  if (!Number.isFinite(lastQuoted)) {
    lastQuoted = getLineQuotedPrice(entry)
  }
  if (!Number.isFinite(lastQuoted)) {
    lastQuoted = getQuotedPriceFromMaterialQuotes(
      supplier?.materialQuotes,
      boqId,
      boqNo
    )
  }
  if (!Number.isFinite(lastQuoted)) lastQuoted = initial

  const difference =
    Number.isFinite(lastQuoted) && Number.isFinite(lastTarget)
      ? lastQuoted - lastTarget
      : NaN

  return { values, lastQuoted, lastTarget, difference }
}

function getRfqTermsRows(rfq) {
  if (Array.isArray(rfq?.terms) && rfq.terms.length > 0) {
    return rfq.terms.map((t) => ({
      rfqTerm: t.rfqTerm || '-',
      termsResponse: t.termsResponse || '-'
    }))
  }
  if (rfq?.rfqTerms) {
    return [{ rfqTerm: rfq.rfqTerms, termsResponse: rfq.termsResponse || '-' }]
  }
  return []
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title }) => (
  <div className="row mb-2 mt-3">
    <div className="col-12">
      <h5 className="text-primary border-bottom pb-2 mb-3">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
  </div>
)

const LrfqView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [rfq, setRfq] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [selectedSupplier, setSelectedSupplier] = useState(null)
  const [showQuoteModal, setShowQuoteModal] = useState(false)
  const [showTargetPriceModal, setShowTargetPriceModal] = useState(false)
  const [targetPriceModalAnimating, setTargetPriceModalAnimating] = useState(false)
  const [priceMatchApplyTo, setPriceMatchApplyTo] = useState('all') // all | supplier
  const [priceMatchSupplierMailId, setPriceMatchSupplierMailId] = useState('')
  const [boqTargetInputs, setBoqTargetInputs] = useState({})
  const [isSavingTargetPrice, setIsSavingTargetPrice] = useState(false)
  const [showTargetPriceSuppliers, setShowTargetPriceSuppliers] = useState(false)
  const [targetPriceModalTab, setTargetPriceModalTab] = useState('history') // history | notify | edit
  const [resendingFor, setResendingFor] = useState('')
  const [sendingPriceMatchFor, setSendingPriceMatchFor] = useState('')
  const [exportingPriceMatching, setExportingPriceMatching] = useState(false)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [additionalCcEmails, setAdditionalCcEmails] = useState('')
  const [ccUserSuggestions, setCcUserSuggestions] = useState([])
  const [isSearchingCcUsers, setIsSearchingCcUsers] = useState(false)
  const ccSearchDebounceRef = useRef(null)
  const ccSearchRequestIdRef = useRef(0)
  const targetPriceModalCloseTimerRef = useRef(null)

  const lrfqOwnerAttachments = useMemo(() => {
    if (!Array.isArray(rfq?.lrfqOwner)) return []
    return rfq.lrfqOwner.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }, [rfq?.lrfqOwner])

  // Strict order: 1) get-lrfq-by-id → 2) get-user (never parallel on same API).
  useEffect(() => {
    if (!id) return

    let cancelled = false

    const loadInOrder = async () => {
      setIsLoading(true)
      try {
        const rfqResponse = await axios.post(API_URL, {
          action: 'get-lrfq-by-id',
          lrfqid: id,
          currentUserId: localStorage.getItem('id') || undefined
        })
        if (cancelled) return

        const responseData = normalizeApiPayload(parseApiResponse(rfqResponse.data))

        if (isApiSuccess(responseData)) {
          const row = pickLrfqFromResponse(responseData, id)
          if (row) {
            setRfq(row)
          } else {
            toast.error('LRFQ not found', { autoClose: 2000 })
            navigate('/logisticsRfq')
            return
          }
        } else {
          toast.error(responseData?.message || 'Failed to fetch LRFQ details', {
            autoClose: 2000
          })
          navigate('/logisticsRfq')
          return
        }
      } catch (error) {
        if (cancelled) return
        console.error('Fetch LRFQ error:', error)
        const errData = error.response?.data ? parseApiResponse(error.response.data) : null
        toast.error(
          errData?.message || 'An error occurred while fetching LRFQ details',
          { autoClose: 2000 }
        )
        navigate('/logisticsRfq')
        return
      } finally {
        if (!cancelled) setIsLoading(false)
      }

      // 2nd call only after RFQ response is done
      const userId = localStorage.getItem('id')
      if (!userId || cancelled) return

      try {
        const userResponse = await axios.post(API_URL, {
          action: 'get-user',
          id: userId
        })
        if (cancelled) return
        const responseData = parseApiResponse(userResponse.data)
        if (isApiSuccess(responseData) && responseData.user?.email) {
          setUserEmail(String(responseData.user.email).trim())
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Fetch user email error:', error)
        }
      }
    }

    loadInOrder()

    return () => {
      cancelled = true
      if (ccSearchDebounceRef.current) {
        clearTimeout(ccSearchDebounceRef.current)
      }
    }
  }, [id, navigate])

  const getCcSearchTerm = (value) => {
    const segments = String(value || '').split(/[,;]/)
    return segments[segments.length - 1].trim()
  }

  const searchCcUsers = async (term, requestId) => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-users',
        page: 1,
        limit: 25,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: term
      })
      if (requestId !== ccSearchRequestIdRef.current) return

      const responseData = parseApiResponse(response.data)
      const users = Array.isArray(responseData?.users) ? responseData.users : []
      setCcUserSuggestions(
        users.filter((user) => isValidEmailAddress(user?.email))
      )
    } catch (error) {
      console.error('Search CC users error:', error)
      if (requestId === ccSearchRequestIdRef.current) {
        setCcUserSuggestions([])
      }
    } finally {
      if (requestId === ccSearchRequestIdRef.current) {
        setIsSearchingCcUsers(false)
      }
    }
  }

  const handleAdditionalCcChange = (e) => {
    const value = e.target.value
    setAdditionalCcEmails(value)

    ccSearchRequestIdRef.current += 1
    const requestId = ccSearchRequestIdRef.current
    if (ccSearchDebounceRef.current) {
      clearTimeout(ccSearchDebounceRef.current)
    }

    const term = getCcSearchTerm(value)
    if (term.length < 3) {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
      return
    }

    setIsSearchingCcUsers(true)
    ccSearchDebounceRef.current = setTimeout(() => {
      searchCcUsers(term, requestId)
    }, 400)
  }

  const handleSelectCcUser = (email) => {
    const selected = String(email || '').trim()
    if (!selected) return

    setAdditionalCcEmails((prev) => {
      const value = String(prev || '')
      const lastSeparator = Math.max(value.lastIndexOf(','), value.lastIndexOf(';'))
      const base = lastSeparator >= 0 ? `${value.slice(0, lastSeparator + 1)} ` : ''
      const existing = parseAdditionalCcEmails(base)
      if (existing.some((item) => item.toLowerCase() === selected.toLowerCase())) {
        return base
      }
      return `${base}${selected}, `
    })
    ccSearchRequestIdRef.current += 1
    setCcUserSuggestions([])
    setIsSearchingCcUsers(false)
  }

  const handleAdditionalCcBlur = () => {
    setTimeout(() => {
      setCcUserSuggestions([])
      setIsSearchingCcUsers(false)
    }, 180)
  }

  const handleResendEmail = async (supplier) => {
    const supplierEmail = String(supplier?.supplierMailId || '').trim()
    if (!id) {
      toast.error('LRFQ ID is missing', { autoClose: 2000 })
      return
    }
    if (!supplierEmail) {
      toast.error('Supplier email is missing', { autoClose: 2000 })
      return
    }
    if (String(rfq?.status).toLowerCase() !== 'active') {
      toast.error('LRFQ must be Active before sending email', { autoClose: 2000 })
      return
    }
    if (!hasLrfqPdfDocument(rfq)) {
      toast.error('PDF is not available for this LRFQ', { autoClose: 2000 })
      return
    }

    const userId = localStorage.getItem('id')
    if (!userId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    const ccEmails = buildCcEmails(userEmail, additionalCcEmails)
    const invalidCc = ccEmails.find((email) => !isValidEmailAddress(email))
    if (invalidCc) {
      toast.error(`Invalid CC email: ${invalidCc}`, { autoClose: 2000 })
      return
    }
    if (ccEmails.length === 0) {
      toast.error('Could not get your email for CC. Add CC emails or check your profile.', {
        autoClose: 2000
      })
      return
    }

    setResendingFor(supplierEmail)
    try {
      const response = await axios.post(API_URL, {
        action: 'resend-lrfq-email',
        id,
        supplierMailId: supplierEmail,
        userid: userId,
        ccEmails
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const result = responseData?.data?.result
        const failCount = Number(responseData?.data?.failCount) || 0
        const sendOk = result?.success !== false && failCount === 0

        if (!sendOk) {
          toast.error(
            result?.message || responseData?.message || 'Failed to resend email',
            { autoClose: 2000 }
          )
          return
        }

        toast.success(
          responseData?.message || `Email resent to ${supplierEmail}`,
          { autoClose: 2000 }
        )
        setRfq((prev) => applyResendEmailToLrfq(prev, responseData, supplierEmail))
      } else {
        toast.error(responseData?.message || 'Failed to resend email', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Resend LRFQ email error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(errData?.message || 'An error occurred while resending email', {
        autoClose: 2000
      })
    } finally {
      setResendingFor('')
    }
  }

  const handleSendPriceMatchingEmail = async (supplier) => {
    const supplierEmail = String(supplier?.supplierMailId || '').trim()
    if (!id) {
      toast.error('LRFQ ID is missing', { autoClose: 2000 })
      return
    }
    if (!supplierEmail) {
      toast.error('Supplier email is missing', { autoClose: 2000 })
      return
    }

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    setSendingPriceMatchFor(supplierEmail)
    try {
      const response = await axios.post(API_URL, {
        action: 'send-lrfq-email-price-matching',
        id,
        supplierMailId: supplierEmail,
        currentUserId
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        toast.success(
          responseData?.message ||
            `Price matching email sent to ${supplierEmail}`,
          { autoClose: 2000 }
        )
      } else {
        toast.error(
          responseData?.message || 'Failed to send price matching email',
          { autoClose: 2000 }
        )
      }
    } catch (error) {
      console.error('Send LRFQ price matching email error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message ||
          'An error occurred while sending price matching email',
        { autoClose: 2000 }
      )
    } finally {
      setSendingPriceMatchFor('')
    }
  }

  const handleExportPriceMatching = async () => {
  if (!id || exportingPriceMatching) return;

  setExportingPriceMatching(true);

  try {
    const response = await axios.post(
      API_URL,
      {
        action: 'export-lrfq-price-matching',
        lrfqid: id,
        currentUserId: localStorage.getItem('id') || undefined,
      },
      { responseType: 'arraybuffer' }
    );

    const responseBytes = new Uint8Array(response.data);

    const isXlsx = (bytes) =>
      bytes.length >= 4 &&
      bytes[0] === 0x50 &&
      bytes[1] === 0x4b &&
      [0x03, 0x05, 0x07].includes(bytes[2]) &&
      [0x04, 0x06, 0x08].includes(bytes[3]);

    let excelBytes = responseBytes;

   let base64 = new TextDecoder()
  .decode(responseBytes)
  .trim();

if (base64.startsWith('"') && base64.endsWith('"')) {
  base64 = JSON.parse(base64);
}

base64 = base64.replace(/^data:.*?;base64,/i, '');

base64 = base64.replace(/\s/g, '');

const decoded = atob(base64);

excelBytes = new Uint8Array(decoded.length);

for (let i = 0; i < decoded.length; i++) {
  excelBytes[i] = decoded.charCodeAt(i);
}

if (
  excelBytes[0] !== 0x50 ||
  excelBytes[1] !== 0x4b
) {
  throw new Error('Invalid Excel workbook received from backend');
}

    console.log('Excel file size:', excelBytes.length);
console.log(
  'Excel signature:',
  Array.from(excelBytes.slice(0, 8))
);
    if (!isXlsx(excelBytes)) {
      console.error('Invalid XLSX signature:', {
        length: excelBytes.length,
        firstBytes: Array.from(excelBytes.slice(0, 20)),
      });

      throw new Error('Downloaded data is not a valid XLSX workbook');
    }

    const disposition = String(
      response.headers?.['content-disposition'] || ''
    );

    const nameMatch = disposition.match(/filename="?([^";]+)"?/i);

    const safeNo = String(lrfqNo || 'LRFQ').replace(
      /[^\w.-]+/g,
      '_'
    );

    const fileName =
      nameMatch?.[1] ||
      `LRFQ_Price_Matching_${safeNo}_${new Date()
        .toISOString()
        .split('T')[0]}.xlsx`;

    const blob = new Blob([excelBytes], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = fileName.endsWith('.xlsx')
      ? fileName
      : `${fileName}.xlsx`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);

    toast.success('Price matching exported successfully', {
      autoClose: 2000,
    });

  } catch (error) {
    console.error('Export LRFQ price matching error:', error);

    toast.error(
      error.message || 'Failed to export price matching',
      { autoClose: 2500 }
    );

  } finally {
    setExportingPriceMatching(false);
  }
};

  const handleCopySupplierLink = async (supplier) => {
    const link = getSupplierEmailLink(id, supplier)
    if (!link) {
      toast.error('Supplier link is not available', { autoClose: 2000 })
      return
    }

    try {
      await navigator.clipboard.writeText(link)
      toast.success('Link copied to clipboard', { autoClose: 2000 })
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = link
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        toast.success('Link copied to clipboard', { autoClose: 2000 })
      } catch {
        toast.error('Failed to copy link', { autoClose: 2000 })
      }
      document.body.removeChild(textarea)
    }
  }

  const openQuoteModal = (supplier) => {
    // Supplier Quote modal only: always list last quoted values.
    const afterRevs = getSupplierAfterTargetRevisions(supplier)
       const lastRev = [...afterRevs].reverse().find(Boolean) || null
    const liveQuotes = Array.isArray(supplier?.materialQuotes)
      ? supplier.materialQuotes
      : []
    const latestQuotes =
      liveQuotes.length > 0
        ? liveQuotes
        : Array.isArray(lastRev?.materialQuotes)
          ? lastRev.materialQuotes
          : []
    setSelectedSupplier({
      ...supplier,
      materialQuotes: enrichMaterialQuotesWithLrfqBoq(latestQuotes, rfq),
      terms:
        Array.isArray(supplier?.terms) && supplier.terms.length > 0
          ? supplier.terms
          : lastRev?.terms || supplier?.terms || [],
      grossTotalBasicPrice:
        supplier?.grossTotalBasicPrice ?? lastRev?.grossTotalBasicPrice,
      grossGstValue: supplier?.grossGstValue ?? lastRev?.grossGstValue,
      grossTotalValue: supplier?.grossTotalValue ?? lastRev?.grossTotalValue,
      quoteSubmittedAt:
        supplier?.quoteSubmittedAt ||
        lastRev?.updatedAt ||
        lastRev?.createdAt ||
        null,
      _historyPhase: undefined
    })
    setShowQuoteModal(true)
  }

  const closeQuoteModal = () => {
    setShowQuoteModal(false)
    setSelectedSupplier(null)
  }

  const openTargetPriceModal = () => {
    const suppliersForGate =
      Array.isArray(rfq?.suppliers) && rfq.suppliers.length > 0
        ? rfq.suppliers
        : rfq?.supplierMailId
          ? [
              {
                supplierName: rfq.supplierName || '-',
                supplierMailId: rfq.supplierMailId,
                phoneNumber: rfq.phoneNumber || ''
              }
            ]
          : []
    if (!rfqHasAnySupplierQuote(rfq, suppliersForGate)) {
      toast.info(
        'Price Matching is available after at least one supplier submits a quote',
        { autoClose: 2500 }
      )
      return
    }
    if (targetPriceModalCloseTimerRef.current) {
      clearTimeout(targetPriceModalCloseTimerRef.current)
      targetPriceModalCloseTimerRef.current = null
    }
    setPriceMatchApplyTo('all')
    setPriceMatchSupplierMailId('')
    setBoqTargetInputs(buildTargetInputsFromLrfq(rfq, null))
    const hasTargets = rfqHasSavedPriceMatchingTargets(rfq, suppliersForGate)
    // Without saved targets, open Edit only — do not list Quote History yet.
    setShowTargetPriceSuppliers(hasTargets)
    setTargetPriceModalTab(hasTargets ? 'history' : 'edit')
    setShowTargetPriceModal(true)
    setTargetPriceModalAnimating(false)
    // Next frame: add `.show` so Bootstrap fade/slide transition runs
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTargetPriceModalAnimating(true)
      })
    })
  }

  const closeTargetPriceModal = (options = {}) => {
    if (isSavingTargetPrice && !options.force) return
    setTargetPriceModalAnimating(false)
    if (targetPriceModalCloseTimerRef.current) {
      clearTimeout(targetPriceModalCloseTimerRef.current)
    }
    targetPriceModalCloseTimerRef.current = setTimeout(() => {
      setShowTargetPriceModal(false)
      setShowTargetPriceSuppliers(false)
      setTargetPriceModalTab('history')
      setPriceMatchApplyTo('all')
      setPriceMatchSupplierMailId('')
      setBoqTargetInputs({})
      targetPriceModalCloseTimerRef.current = null
    }, 200)
  }

  const handleBoqTargetInputChange = (key, value) => {
    if (!isDecimalInput(value)) return
    setBoqTargetInputs((prev) => ({ ...prev, [key]: value }))
  }

  const handlePriceMatchApplyToChange = (nextApplyTo) => {
    setPriceMatchApplyTo(nextApplyTo)
    if (nextApplyTo === 'all') {
      setPriceMatchSupplierMailId('')
      setBoqTargetInputs(buildTargetInputsFromLrfq(rfq, null))
      return
    }
    const firstQuoted = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) => supplierHasQuotedPrice(s) && String(s.supplierMailId || '').trim()
    )
    const mail = String(firstQuoted?.supplierMailId || '').trim()
    setPriceMatchSupplierMailId(mail)
    const selected = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) =>
        String(s.supplierMailId || '').trim().toLowerCase() === mail.toLowerCase()
    )
    setBoqTargetInputs(buildTargetInputsFromLrfq(rfq, selected || null))
  }

  const handlePriceMatchSupplierChange = (mailId) => {
    const mail = String(mailId || '').trim()
    setPriceMatchSupplierMailId(mail)
    const selected = (Array.isArray(rfq?.suppliers) ? rfq.suppliers : []).find(
      (s) =>
        String(s.supplierMailId || '').trim().toLowerCase() === mail.toLowerCase()
    )
    setBoqTargetInputs(buildTargetInputsFromLrfq(rfq, selected || null))
  }

  const refreshLrfqAfterTargetPrice = async () => {
    try {
      const response = await axios.post(API_URL, {
        action: 'get-lrfq-by-id',
        lrfqid: id,
        currentUserId: localStorage.getItem('id') || undefined
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) return null
      const row = pickLrfqFromResponse(responseData, id)
      if (row) {
        setRfq(row)
        return row
      }
    } catch (error) {
      console.error('Refresh LRFQ after target price error:', error)
    }
    return null
  }

  useEffect(() => {
    return () => {
      if (targetPriceModalCloseTimerRef.current) {
        clearTimeout(targetPriceModalCloseTimerRef.current)
      }
    }
  }, [])


  const handleSaveTargetPrice = async () => {
    if (!id || isSavingTargetPrice) return

    const currentUserId = localStorage.getItem('id')
    if (!currentUserId) {
      toast.error('User session expired. Please login again.', { autoClose: 2000 })
      return
    }

    if (!isLrfqDueDatePassed(rfq?.lrfqDueDate)) {
      toast.error(TARGET_PRICE_BEFORE_DUE_MSG, { autoClose: 3500 })
      return
    }

    const boqRows = collectLrfqBoqTargetRows(rfq)
    const boqTargetPrices = []
    for (const row of boqRows) {
      const raw = String(boqTargetInputs[row.key] ?? '').trim()
      if (!raw) continue
      const parsedBoq = parseDecimalValue(raw)
      if (!Number.isFinite(parsedBoq) || parsedBoq < 0) {
        toast.error(
          `Enter a valid target for ${row.boqNo || row.key}`,
          { autoClose: 2200 }
        )
        return
      }
      if (!row.boq_id) {
        toast.error(`Line ${row.boqNo || row.key} has no unique ID`, {
          autoClose: 2200
        })
        return
      }
      boqTargetPrices.push({ uniqueId: row.boq_id, targetPrice: parsedBoq })
    }

    const applyTo = priceMatchApplyTo === 'supplier' ? 'supplier' : 'all'

    if (applyTo === 'supplier') {
      if (!String(priceMatchSupplierMailId || '').trim()) {
        toast.error('Select a supplier for price matching', { autoClose: 2000 })
        return
      }
      if (boqTargetPrices.length === 0) {
        toast.error('Enter at least one target price for this supplier', {
          autoClose: 2200
        })
        return
      }
    } else if (boqTargetPrices.length === 0) {
      toast.error('Enter at least one target price', { autoClose: 2200 })
      return
    }

    const payload = {
      action: 'update-lrfq-target-price',
      id,
      lrfqid: id,
      applyTo,
      currentUserId,
      targetPrice: boqTargetPrices
    }
    if (applyTo === 'supplier') {
      payload.supplierMailId = String(priceMatchSupplierMailId).trim()
    }

    setIsSavingTargetPrice(true)
    try {
      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (!isApiSuccess(responseData)) {
        toast.error(
          responseData?.message || 'Failed to update price matching value',
          { autoClose: 2500 }
        )
        setIsSavingTargetPrice(false)
        return
      }


      const dataBlock =
        responseData?.data && typeof responseData.data === 'object'
          ? responseData.data
          : {}

      setRfq((prev) =>
        prev
          ? {
              ...prev,
              ...dataBlock,
              updatedAt: dataBlock.updatedAt || prev.updatedAt
            }
          : prev
      )

      await refreshLrfqAfterTargetPrice()

      const appliedList = Array.isArray(dataBlock.appliedToSuppliers)
        ? dataBlock.appliedToSuppliers
        : []
      const skippedList = Array.isArray(dataBlock.skippedSuppliers)
        ? dataBlock.skippedSuppliers
        : []
      const appliedCount =
        Number(dataBlock.appliedToSuppliersCount) ||
        Number(dataBlock.appliedCount) ||
        appliedList.length
      const skippedCount =
        Number(dataBlock.skippedSuppliersCount) ||
        Number(dataBlock.skippedCount) ||
        skippedList.length

      let successMsg =
        responseData?.message || 'Price matching value updated successfully'
      if (applyTo === 'all' && (appliedCount > 0 || skippedCount > 0)) {
        successMsg = `${successMsg} Applied to ${appliedCount} quoted supplier${
          appliedCount === 1 ? '' : 's'
        }; skipped ${skippedCount} without quote.`
      }
      if (appliedCount > 0) {
        successMsg = `${successMsg} Quoted suppliers have 4 days (targetPriceDueDate) to counter-offer.`
      }

      toast.success(successMsg, { autoClose: 4000 })
      setShowTargetPriceSuppliers(true)
      setTargetPriceModalTab('notify')
      setIsSavingTargetPrice(false)
    } catch (error) {
      console.error('Update LRFQ price matching error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message ||
          error.message ||
          'An error occurred while updating price matching value',
        { autoClose: 2500 }
      )
      setIsSavingTargetPrice(false)
    }
  }

  const downloadLrfqOwnerAttachment = async (s3Key, fallbackUrl, originalFileName) => {
    if (!s3Key || downloadingS3Key) return
    setDownloadingS3Key(s3Key)
    try {
      const fileName = String(originalFileName || '').trim()
      const response = await axios.post(API_URL, {
        action: 'get-lrfq-owner-attachment-download-presigned-url',
        s3Key,
        s3Keys: [s3Key],
        ...(fileName
          ? { originalFileName: fileName, originalFileNames: [fileName] }
          : {}),
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const matched =
        (Array.isArray(responseData.files) ? responseData.files : []).find(
          (f) => String(f?.s3Key || '') === String(s3Key)
        ) || responseData.files?.[0]
      const downloadUrl =
        matched?.downloadUrl || responseData.downloadUrl || fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download LRFQ owner attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const downloadSupplierAttachment = async (s3Key, fallbackUrl, supplierEmail) => {
    if (!s3Key || downloadingS3Key) return
    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-lrfq-supplier-attachment-download-presigned-url',
        lrfqNumber: rfq?.lrfqNumber || rfq?.LrfqNumber || '',
        lrfqid: id,
        email: String(supplierEmail || '').trim(),
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl || fallbackUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download supplier attachment error:', error)
      if (fallbackUrl) {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('Failed to download attachment', { autoClose: 2000 })
      }
    } finally {
      setDownloadingS3Key('')
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-6 col-sm-6">
            <h1 className="m-0 txt_lft">View LRFQ</h1>
          </div>
          <div className="col-6 col-sm-6">
            <ol className="breadcrumb justify-content-end mb-0">
              <li className="breadcrumb-item">
                <Link to="/logisticsRfq">
                  <i className="fa-solid fa-arrow-left-long" /> Back
                </Link>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!rfq) {
    return (
      <div>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p>LRFQ not found</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const materials = Array.isArray(rfq.materials) ? rfq.materials : []
  const lrfqTermsRows = getRfqTermsRows(rfq)
  const suppliers =
    Array.isArray(rfq.suppliers) && rfq.suppliers.length > 0
      ? rfq.suppliers
      : rfq.supplierMailId
        ? [{ supplierName: rfq.supplierName || '-', supplierMailId: rfq.supplierMailId, phoneNumber: rfq.phoneNumber || '' }]
        : []
  const targetPriceHistory = getTargetPriceQuoteHistory(rfq)
  const canUsePriceMatching = rfqHasAnySupplierQuote(rfq, suppliers)
  const lrfqDueDatePassed = isLrfqDueDatePassed(rfq.lrfqDueDate)
  const canEditTargetPrice = canUsePriceMatching && lrfqDueDatePassed
  const savedTargetCount = Object.keys(getLrfqTargetMap(rfq)).length
  const hasAnyPriceMatchingTarget = rfqHasSavedPriceMatchingTargets(
    rfq,
    suppliers
  )
  const boqTargetRows = collectLrfqBoqTargetRows(rfq)
  const priceMatchSupplierOptions = suppliers.filter(
    (s) => String(s.supplierMailId || '').trim() && supplierHasQuotedPrice(s)
  )
  const lrfqCurrency = rfq.lrfqCurrency || rfq.LrfqCurrency || ''
  const currencyLabel = lrfqCurrency ? ` ${lrfqCurrency}` : ''
  const lrfqNo = rfq.lrfqNumber || rfq.LrfqNumber || '-'

  const quotedSuppliersForPriceMatch = suppliers.filter((s) =>
    supplierHasQuotedPrice(s)
  )
  const priceMatchingSuppliersList = getPriceMatchingSuppliersList(rfq, suppliers)
  const matchedSuppliersCount = quotedSuppliersForPriceMatch.filter(
    (s) =>
      !priceMatchingSuppliersList.some((pm) => suppliersMatchIdentity(pm, s))
  ).length
  const boqPriceMatchingLines = getResolvedBoqPriceMatchingLines(rfq, suppliers)
  const needsMatchingBoqLineCount = boqPriceMatchingLines.filter((line) => {
    if (line?.needsPriceMatching === true) return true
    if (line?.needsPriceMatching === false) return false
    return getBoqLineSupplierEntries(line).some(lineNeedsPriceMatching)
  }).length
  const priceMatchingHistoryColumns = buildLrfqPriceMatchingHistoryColumnDefs(
    rfq,
    boqPriceMatchingLines,
    suppliers
  )
  const priceMatchingHistoryGroups = groupBoqPriceMatchingLinesByParent(
    boqPriceMatchingLines,
    materials,
    suppliers
  )
  // Quote History alignment: all lanes for supplier A, then supplier B, …
  const priceMatchingHistorySupplierSections = (() => {
    const groups = priceMatchingHistoryGroups
    const order = []
    const seen = new Set()
    const pushIdentity = (identity) => {
      if (!identity) return
      const mail = String(identity.supplierMailId || '')
        .trim()
        .toLowerCase()
      const sid = String(identity.supplierId || '').trim()
      const key = mail || sid
      if (!key || seen.has(key)) return
      const appears = groups.some((group) =>
        (group.lines || []).some((line) =>
          getBoqLineSupplierEntries(line).some((entry) =>
            suppliersMatchIdentity(entry, identity)
          )
        )
      )
      if (!appears) return
      seen.add(key)
      const match = suppliers.find((s) => suppliersMatchIdentity(s, identity))
      order.push({ key, supplier: match || identity })
    }
    suppliers.forEach(pushIdentity)
    groups.forEach((group) => {
      ;(group.lines || []).forEach((line) => {
        getBoqLineSupplierEntries(line).forEach(pushIdentity)
      })
    })
    return order.length > 0
      ? order
      : [{ key: '__none__', supplier: null }]
  })()

  const renderPriceMatchingEditForm = (inputIdPrefix = 'lrfqTargetPrice') => (
    <>
      <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
        Set price matching for{' '}
        <strong>{lrfqNo !== '-' ? lrfqNo : 'this LRFQ'}</strong>
        {lrfqCurrency ? ` (${lrfqCurrency})` : ''}. Apply the same lane / material
        targets to all <em>quoted</em> suppliers, or to one supplier only.
      </p>

      <div className="form-group mb-3">
        <label className="d-block mb-2">Apply To</label>
        <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
          <label className="mb-0" style={{ fontWeight: 500, cursor: 'pointer' }}>
            <input
              type="radio"
              className="mr-1"
              name={`${inputIdPrefix}-applyTo`}
              checked={priceMatchApplyTo === 'all'}
              onChange={() => handlePriceMatchApplyToChange('all')}
              disabled={isSavingTargetPrice}
            />
            All quoted suppliers
          </label>
          <label className="mb-0" style={{ fontWeight: 500, cursor: 'pointer' }}>
            <input
              type="radio"
              className="mr-1"
              name={`${inputIdPrefix}-applyTo`}
              checked={priceMatchApplyTo === 'supplier'}
              onChange={() => handlePriceMatchApplyToChange('supplier')}
              disabled={isSavingTargetPrice}
            />
            One supplier
          </label>
        </div>
      </div>

      {priceMatchApplyTo === 'supplier' ? (
        <div className="form-group mb-3">
          <label htmlFor={`${inputIdPrefix}-supplier`}>Supplier</label>
          <select
            id={`${inputIdPrefix}-supplier`}
            className="form-control form-control-sm"
            value={priceMatchSupplierMailId}
            onChange={(e) => handlePriceMatchSupplierChange(e.target.value)}
            disabled={isSavingTargetPrice}
          >
            <option value="">Select supplier</option>
            {(priceMatchSupplierOptions.length > 0
              ? priceMatchSupplierOptions
              : suppliers
            ).map((s) => (
              <option
                key={s.supplierId || s.supplierMailId}
                value={String(s.supplierMailId || '').trim()}
              >
                {s.supplierName || s.supplierMailId}
                {s.supplierMailId ? ` (${s.supplierMailId})` : ''}
                {supplierNeedsPriceMatching(s) ? ' — needs matching' : ''}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {boqTargetRows.length === 0 ? (
        <div className="alert alert-light border mb-0" style={{ fontSize: '13px' }}>
          No materials or BOQ lanes with a unique ID on this LRFQ.
        </div>
      ) : (
        <div className="table-responsive mb-0">
          <table className="table table-bordered table-sm mb-0">
            <thead>
              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                <th style={{ fontSize: '12px' }}>S.No</th>
                <th style={{ fontSize: '12px' }}>Materials &amp; Lines</th>
                <th style={{ fontSize: '12px' }}>Qty</th>
                <th style={{ fontSize: '12px', minWidth: '120px' }}>
                  Target Price
                </th>
              </tr>
            </thead>
            <tbody>
              {boqTargetRows.map((row) => (
                <tr key={row.key} style={{ backgroundColor: '#fff8e6' }}>
                  <td className="fwt_600" style={{ color: '#856404' }}>
                    {row.boqNo}
                  </td>
                  <td
                    style={{
                      maxWidth: '240px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}
                  >
                    {row.materialDescription}
                    {row.parentDescription ? (
                      <div className="text-muted" style={{ fontSize: '11px' }}>
                        {row.parentDescription}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {row.quantity ?? '-'}
                    {row.uom ? ` ${row.uom}` : ''}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={boqTargetInputs[row.key] ?? ''}
                      onChange={(e) =>
                        handleBoqTargetInputChange(row.key, e.target.value)
                      }
                      disabled={isSavingTargetPrice}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )

  return (
    <div>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="LRFQ Details" />

                    <ViewField label="LRFQ Number">{lrfqNo}</ViewField>
                    <ViewField label="LRFQ Date">{formatDate(rfq.lrfqDate)}</ViewField>
                    <ViewField label="Due Date">{formatDateTime(rfq.lrfqDueDate)}</ViewField>
                    <ViewField label="Currency">{lrfqCurrency || '-'}</ViewField>
                    <ViewField label="LRFQ Status">
                      <span className="fwt_600">{capitalizeStatus(rfq.lrfqStatus)}</span>
                    </ViewField>
                    <ViewField label="Status">
                      <span
                        className={`badge ${
                          String(rfq.status).toLowerCase() === 'active'
                            ? 'badge-success'
                            : 'badge-secondary'
                        }`}
                        style={{ fontSize: '90%', fontWeight: 550 }}
                      >
                        {capitalizeStatus(rfq.status)}
                      </span>
                    </ViewField>
                    <ViewField label="Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{rfq.lrfqSummary || '-'}</span>
                    </ViewField>

                    <SectionTitle icon="fa-list-check" title="Terms" />

                    {lrfqTermsRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '160px' }}>LRFQ Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Terms Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lrfqTermsRows.map((term, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td
                                  style={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.rfqTerm}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {term.termsResponse}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-boxes-stacked" title="Materials" />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Cluster</th>
                              <th style={{ fontSize: '12px' }}>Cluster Description</th>
                              <th style={{ fontSize: '12px' }}>HSN</th>
                              <th style={{ fontSize: '12px' }}>UOM</th>
                              <th style={{ fontSize: '12px' }}>Qty</th>
                            </tr>
                          </thead>
                          <tbody>
                            {materials.flatMap((row, index) => {
                              const lineNo = index + 1
                              const lanes = getMaterialBoqLines(row)
                              const parentRow = (
                                <tr key={`mat-${index}`}>
                                  <td className="fwt_600">
                                    {lineNo}
                                    {lanes.length > 0 ? (
                                      <span
                                        className="badge badge-warning ml-1"
                                        style={{ fontSize: '9px' }}
                                      >
                                        BOQ
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{row.sourceCode || '-'}</td>
                                  <td
                                    style={{
                                      maxWidth: '280px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {row.sourceDescription || '-'}
                                  </td>
                                  <td>{row.hsn || row.hsnCode || '-'}</td>
                                  <td>{row.uom || '-'}</td>
                                  <td>{row.quantity ?? '-'}</td>
                                </tr>
                              )

                              const laneRow =
                                lanes.length > 0 ? (
                                  <tr
                                    key={`mat-${index}-lanes`}
                                    style={{ backgroundColor: '#fff8e6' }}
                                  >
                                    <td />
                                    <td colSpan={5} style={{ padding: '8px' }}>
                                      <div style={{ overflowX: 'auto' }}>
                                        <table className="table table-sm table-bordered mb-0">
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: '#856404',
                                                color: '#fff'
                                              }}
                                            >
                                              {[
                                                'S.No',
                                                'Source Pincode',
                                                'Destination Pincode',
                                                'Source City',
                                                'Destination City',
                                                'Truck Name',
                                                'Truck Body',
                                                'Loadability (MT)',
                                                'Length (Ft)',
                                                'Width (Ft)',
                                                'Height (Ft)'
                                              ].map((label) => (
                                                <th
                                                  key={label}
                                                  style={{
                                                    fontSize: '11px',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {label}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {lanes.map((lane, laneIndex) => (
                                              <tr
                                                key={`mat-${index}-lane-${lane.uniqueId || laneIndex}`}
                                              >
                                                <td
                                                  style={{
                                                    fontWeight: 600,
                                                    color: '#856404',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {getBoqLineNo(
                                                    lane,
                                                    `${lineNo}.${laneIndex + 1}`
                                                  )}
                                                </td>
                                                <td>{lane.sourcePincode || '-'}</td>
                                                <td>{lane.destinationPincode || '-'}</td>
                                                <td>{lane.sourceCity || '-'}</td>
                                                <td>{lane.destinationCity || '-'}</td>
                                                <td>{lane.truckName || '-'}</td>
                                                <td>{lane.truckBody || '-'}</td>
                                                <td>{lane.truckLoadabilityMt ?? '-'}</td>
                                                <td>{lane.truckLengthFt ?? '-'}</td>
                                                <td>{lane.truckWidthFt ?? '-'}</td>
                                                <td>{lane.truckHeightFt ?? '-'}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null

                              return laneRow ? [parentRow, laneRow] : [parentRow]
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle icon="fa-envelope" title="Supplier Email" />

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <div className="form-group mb-2">
                          <label>CC Emails (your email)</label>
                          <div className="txtdsply" style={{ fontSize: '13px' }}>
                            {userEmail || (
                              <span className="text-muted">
                                Profile email not available — add CC addresses below
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group mb-2" style={{ position: 'relative' }}>
                          <label>Additional CC Emails</label>
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Type 3+ letters to search users"
                            value={additionalCcEmails}
                            onChange={handleAdditionalCcChange}
                            onBlur={handleAdditionalCcBlur}
                            disabled={Boolean(resendingFor)}
                            autoComplete="off"
                          />
                          {(isSearchingCcUsers || ccUserSuggestions.length > 0) && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                zIndex: 1060,
                                backgroundColor: '#fff',
                                border: '1px solid #d8e0ea',
                                borderRadius: '8px',
                                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
                                maxHeight: '210px',
                                overflowY: 'auto',
                                marginTop: '2px'
                              }}
                            >
                              {isSearchingCcUsers ? (
                                <div
                                  className="text-muted"
                                  style={{ padding: '10px 12px', fontSize: '13px' }}
                                >
                                  <i className="fa-solid fa-spinner fa-spin mr-2" />
                                  Searching users...
                                </div>
                              ) : (() => {
                                const existing = new Set(
                                  buildCcEmails(userEmail, additionalCcEmails).map(
                                    (item) => item.toLowerCase()
                                  )
                                )
                                const visible = ccUserSuggestions.filter(
                                  (user) =>
                                    !existing.has(String(user.email).trim().toLowerCase())
                                )
                                if (visible.length === 0) {
                                  return (
                                    <div
                                      className="text-muted"
                                      style={{ padding: '10px 12px', fontSize: '13px' }}
                                    >
                                      No matching users found
                                    </div>
                                  )
                                }
                                return visible.map((user) => (
                                  <button
                                    key={user.id || user.email}
                                    type="button"
                                    className="dropdown-item"
                                    style={{
                                      display: 'block',
                                      width: '100%',
                                      textAlign: 'left',
                                      padding: '8px 12px',
                                      whiteSpace: 'normal',
                                      cursor: 'pointer'
                                    }}
                                    onMouseDown={(e) => {
                                      e.preventDefault()
                                      handleSelectCcUser(user.email)
                                    }}
                                  >
                                    <span
                                      className="d-block fwt_600"
                                      style={{ fontSize: '13px' }}
                                    >
                                      {user.name || user.email}
                                    </span>
                                    <small className="text-muted">{user.email}</small>
                                  </button>
                                ))
                              })()}
                            </div>
                          )}
                          <small className="form-text text-muted">
                           select a user, then continue after
                            the comma for the next email.
                          </small>
                          {buildCcEmails(userEmail, additionalCcEmails).length > 0 && (
                            <small className="form-text text-muted d-block">
                              Will CC: {buildCcEmails(userEmail, additionalCcEmails).join(', ')}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>

                    {suppliers.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No suppliers</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px' }}>S.No</th>
                              <th style={{ fontSize: '12px' }}>Supplier Name</th>
                              <th style={{ fontSize: '12px' }}>Email</th>
                              <th style={{ fontSize: '12px' }}>City</th>
                              <th style={{ fontSize: '12px' }}>Phone Number</th>
                              <th style={{ fontSize: '12px', minWidth: '130px' }}>
                                PM Due
                              </th>
                              <th style={{ fontSize: '12px', width: '90px' }}>Link</th>
                              <th style={{ fontSize: '12px', width: '90px' }}>View</th>
                              <th style={{ fontSize: '12px', width: '100px' }}>Resend</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suppliers.map((row, index) => {
                              const viewBtn = getSupplierViewButtonProps(row)
                              const supplierEmail = String(row.supplierMailId || '').trim()
                              const isResending = resendingFor === supplierEmail
                              const targetDue = getSupplierTargetPriceDueDate(row)
                              const targetDueActive = isTargetPriceDueDateActive(targetDue)
                              return (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      maxWidth: '220px',
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {row.supplierName || '-'}
                                    {supplierNeedsPriceMatching(row) ? (
                                      <span
                                        className="badge badge-warning ml-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        Needs matching
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{row.supplierMailId || '-'}</td>
                                  <td>{row.city || '-'}</td>
                                  <td>{row.phoneNumber || row.phonenumber || '-'}</td>
                                  <td style={{ fontSize: '12px' }}>
                                    {targetDue ? (
                                      <>
                                        <div>{formatDateTime(targetDue)}</div>
                                        <span
                                          className={`badge ${
                                            targetDueActive
                                              ? 'badge-success'
                                              : 'badge-secondary'
                                          }`}
                                          style={{ fontSize: '10px' }}
                                        >
                                          {targetDueActive ? 'Open' : 'Closed'}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      title="Copy supplier link"
                                      onClick={() => handleCopySupplierLink(row)}
                                      disabled={!getSupplierEmailLink(id, row)}
                                    >
                                      <i className="fa-solid fa-copy" />
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className={viewBtn.className}
                                      title={viewBtn.title}
                                      onClick={() => openQuoteModal(row)}
                                    >
                                      <i className="fa-solid fa-eye" />
                                    </button>
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      title="Resend LRFQ email to supplier"
                                      onClick={() => handleResendEmail(row)}
                                      disabled={
                                        !supplierEmail ||
                                        isResending ||
                                        String(rfq.status).toLowerCase() !== 'active' ||
                                        !hasLrfqPdfDocument(rfq)
                                      }
                                    >
                                      {isResending ? (
                                        <i className="fa-solid fa-spinner fa-spin" />
                                      ) : (
                                        <i className="fa-solid fa-paper-plane" />
                                      )}
                                    </button>
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <ViewField label="Mail Sent">
                      {rfq.isMailSent ? (
                        <span className="badge badge-success">Yes</span>
                      ) : (
                        <span className="badge badge-secondary">No</span>
                      )}
                    </ViewField>

                    <ViewField label="Price Matching">
                     <div className="d-flex flex-wrap align-items-center" style={{ gap: '8px' }}>
                       <Link to={`/logisticsRfqComparison/${id}`} className="btn btn-blue btn-sm">
                        <i className="fa-solid fa-chart-column mr-1" />
                          View Comparison
                        </Link>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={openTargetPriceModal}
                        disabled={!canUsePriceMatching}
                        title={
                          !canUsePriceMatching
                            ? 'Price Matching is available after at least one supplier submits a quote'
                            : !lrfqDueDatePassed
                              ? TARGET_PRICE_BEFORE_DUE_MSG
                              : 'Set or update price matching value'
                        }
                      >
                        <i className="fa-solid fa-bullseye mr-1" />
                        Price Matching
                        {hasAnyPriceMatchingTarget && savedTargetCount > 0 ? (
                          <span className="ml-1">({savedTargetCount})</span>
                        ) : null}
                      </button>
                     </div>
                    </ViewField>

                    {(hasLrfqPdfDocument(rfq) || lrfqOwnerAttachments.length > 0) && (
                      <>
                        <SectionTitle icon="fa-file-pdf" title="Document" />
                        {getLrfqPdfPreviewUrl(rfq) ? (
                          <ViewField label="PDF">
                            <a
                              href={getLrfqPdfPreviewUrl(rfq)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View PDF
                            </a>
                          </ViewField>
                        ) : rfq.pdfLink ? (
                          <ViewField label="PDF">
                            <span className="text-muted">
                              PDF is stored but a download URL is not available.
                              Refresh the page to get a preview link.
                            </span>
                          </ViewField>
                        ) : null}

                        {lrfqOwnerAttachments.length > 0 ? (
                          <ViewField label="LRFQ Owner Attachments">
                            <AttachmentTileGrid
                              attachments={lrfqOwnerAttachments}
                              downloadingS3Key={downloadingS3Key}
                              onDownload={downloadLrfqOwnerAttachment}
                              disabled={Boolean(downloadingS3Key)}
                            />
                          </ViewField>
                        ) : null}
                      </>
                    )}

                    <SectionTitle icon="fa-clock" title="Audit" />

                    <ViewField label="Created By">{displayUser(rfq.createdBy)}</ViewField>
                    <ViewField label="Updated By">{displayUser(rfq.updatedBy)}</ViewField>
                    <ViewField label="Created At">{formatDateTime(rfq.createdAt)}</ViewField>
                    <ViewField label="Updated At">{formatDateTime(rfq.updatedAt)}</ViewField>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showQuoteModal && selectedSupplier && (
        <div
          className="modal fade show"
          style={{ display: 'block' }}
          tabIndex="-1"
          role="dialog"
          onClick={closeQuoteModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Supplier Quote — {selectedSupplier.supplierName || '-'}
                  {selectedSupplier._historyPhase ? (
                    <span
                      className={`badge ml-2 ${
                        String(selectedSupplier._historyPhase).includes('before')
                          ? 'badge-warning'
                          : 'badge-success'
                      }`}
                      style={{ fontSize: '11px', fontWeight: 500 }}
                    >
                      {String(selectedSupplier._historyPhase)
                        .replace(/_/g, ' ')}
                    </span>
                  ) : null}
                </h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={closeQuoteModal}
                >
                  <i className="fa-solid fa-xmark" />
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <div className="row mb-3">
                  <div className="col-md-6">
                    <strong>Email:</strong>{' '}
                    {selectedSupplier.supplierMailId || '-'}
                  </div>
                  <div className="col-md-6 text-md-right">
                    <strong>Submitted At:</strong>{' '}
                    {formatDateTime(selectedSupplier.quoteSubmittedAt)}
                  </div>
                </div>

                {hasSupplierQuotes(selectedSupplier) ? (
                  <>
                    {selectedSupplier.materialQuotes?.length > 0 ? (
                      <>
                        <h6 className="text-primary mb-2">
                          <i className="fa-solid fa-boxes-stacked mr-1" />
                          Materials
                        </h6>
                        <div className="table-responsive mb-4" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                <th style={{ fontSize: '12px' }}>S.No</th>
                                <th style={{ fontSize: '12px' }}>Cluster</th>
                                <th style={{ fontSize: '12px', minWidth: '200px' }}>
                                   Cluster Description
                                </th>
                                <th style={{ fontSize: '12px', minWidth: '80px' }}>HSN</th>
                                <th style={{ fontSize: '12px' }}>UOM</th>
                                <th style={{ fontSize: '12px' }}>Qty</th>
                                <th style={{ fontSize: '12px' }}>Basic Price</th>
                                <th style={{ fontSize: '12px' }}>Total Value</th>
                                <th style={{ fontSize: '12px', minWidth: '140px' }}>
                                  Technical Response
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedSupplier.materialQuotes.flatMap((quote, index) => {
                                const lineNo =
                                  quote.lineNo != null ? quote.lineNo : index + 1
                                const lanes = getMaterialBoqLines(quote)
                                const hasBoq = lanes.length > 0

                                const parentRow = (
                                  <tr key={`quote-${index}`}>
                                    <td className="fwt_600">
                                      {lineNo}
                                      {hasBoq ? (
                                        <span
                                          className="badge badge-warning ml-1"
                                          style={{ fontSize: '9px' }}
                                        >
                                          BOQ
                                        </span>
                                      ) : null}
                                    </td>
                                    <td>{quote.sourceCode || '-'}</td>
                                    <td
                                      style={{
                                        maxWidth: '220px',
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {quote.sourceDescription || '-'}
                                    </td>
                                    <td>{displayQuoteHsn(quote)}</td>
                                    <td>{quote.uom || '-'}</td>
                                    <td>{quote.quantity ?? '-'}</td>
                                    <td className="text-right fwt_600">
                                      {formatAmount(quote.basicPrice)}
                                    </td>
                                    <td className="text-right fwt_600">
                                      {formatAmount(quote.totalValue)}
                                    </td>
                                    <td
                                      style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {quote.comments ||
                                        quote.technicalResponse ||
                                        '-'}
                                    </td>
                                  </tr>
                                )

                                // Nested BOQ logistics table (same as Materials section) + last quoted prices.
                                const laneRow = hasBoq ? (
                                  <tr
                                    key={`quote-${index}-lanes`}
                                    style={{ backgroundColor: '#fff8e6' }}
                                  >
                                    <td />
                                    <td colSpan={8} style={{ padding: '8px' }}>
                                      <div style={{ overflowX: 'auto' }}>
                                        <table className="table table-sm table-bordered mb-0">
                                          <thead>
                                            <tr
                                              style={{
                                                backgroundColor: '#856404',
                                                color: '#fff'
                                              }}
                                            >
                                              {[
                                                'S.No',
                                                'Source Pincode',
                                                'Destination Pincode',
                                                'Source City',
                                                'Destination City',
                                                'Truck Name',
                                                'Truck Body',
                                                'Loadability (MT)',
                                                'Length (Ft)',
                                                'Width (Ft)',
                                                'Height (Ft)',
                                                'Qty',
                                                'Basic Price'
                                              ].map((label) => (
                                                <th
                                                  key={label}
                                                  style={{
                                                    fontSize: '11px',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {label}
                                                </th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {lanes.map((boqItem, boqIndex) => (
                                              <tr
                                                key={`quote-${index}-boq-${getBoqLineId(boqItem) || boqIndex}`}
                                              >
                                                <td
                                                  style={{
                                                    fontWeight: 600,
                                                    color: '#856404',
                                                    whiteSpace: 'nowrap'
                                                  }}
                                                >
                                                  {getBoqLineNo(
                                                    boqItem,
                                                    `${lineNo}.${boqIndex + 1}`
                                                  )}
                                                </td>
                                                <td>
                                                  {boqItem.sourcePincode || '-'}
                                                </td>
                                                <td>
                                                  {boqItem.destinationPincode ||
                                                    '-'}
                                                </td>
                                                <td>
                                                  {boqItem.sourceCity || '-'}
                                                </td>
                                                <td>
                                                  {boqItem.destinationCity ||
                                                    '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckName || '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckBody || '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckLoadabilityMt ??
                                                    '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckLengthFt ?? '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckWidthFt ?? '-'}
                                                </td>
                                                <td>
                                                  {boqItem.truckHeightFt ?? '-'}
                                                </td>
                                                <td>
                                                  {resolveBoqQuantityForDisplay(
                                                    boqItem,
                                                    rfq
                                                  )}
                                                </td>
                                                <td className="text-right fwt_600">
                                                  {formatAmount(
                                                    boqItem.basicPrice
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </td>
                                  </tr>
                                ) : null

                                return laneRow
                                  ? [parentRow, laneRow]
                                  : [parentRow]
                              })}
                              <tr style={{ backgroundColor: '#f4f6f9' }}>
                                <td colSpan={6} className="text-right fwt_600">
                                  Gross Total
                                  {lrfqCurrency ? ` (${lrfqCurrency})` : ''}
                                </td>
                                <td className="text-right fwt_600">
                                  {formatAmount(
                                    selectedSupplier.grossTotalBasicPrice
                                  )}
                                </td>
                                <td className="text-right fwt_600">
                                  {formatAmount(selectedSupplier.grossTotalValue)}
                                </td>
                                <td />
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}

                    {getSupplierTerms(selectedSupplier).length > 0 ? (
                      <>
                        <h6 className="text-primary mb-2">
                          <i className="fa-solid fa-list-check mr-1" />
                          Terms
                        </h6>
                        <div className="table-responsive" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                          <table className="table table-bordered table-sm mb-0">
                            <thead>
                              <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                                <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>LRFQ Term</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>Terms Response</th>
                                <th style={{ fontSize: '12px', minWidth: '160px' }}>Quotes Response</th>
                              </tr>
                            </thead>
                            <tbody>
                              {getSupplierTerms(selectedSupplier).map((term, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td
                                    style={{
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.rfqTerm || '-'}
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.termsResponse || '-'}
                                  </td>
                                  <td
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {term.comments || term.termsComments || '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : null}
                  </>
                ) : (
                  <p className="text-muted mb-0">No quotation submitted yet.</p>
                )}

                {getSupplierAttachments(selectedSupplier).length > 0 ? (
                  <>
                    <h6 className="text-primary mb-2 mt-3">
                      <i className="fa-solid fa-paperclip mr-1" />
                      Attachments
                    </h6>
                    <AttachmentTileGrid
                      attachments={getSupplierAttachments(selectedSupplier)}
                      downloadingS3Key={downloadingS3Key}
                      onDownload={(s3Key, url) =>
                        downloadSupplierAttachment(
                          s3Key,
                          url,
                          selectedSupplier.supplierMailId
                        )
                      }
                      disabled={Boolean(downloadingS3Key)}
                    />
                  </>
                ) : null}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeQuoteModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showQuoteModal && <div className="modal-backdrop fade show" />}

      {showTargetPriceModal ? (
        <div
          className={`modal fade${targetPriceModalAnimating ? ' show' : ''}`}
          style={{ display: 'block' }}
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`modal-dialog modal-dialog-centered modal-dialog-scrollable${
              showTargetPriceSuppliers ? ' modal-xl' : ''
            }`}
            role="document"
            style={{
              transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
              transform: targetPriceModalAnimating
                ? 'none'
                : 'translate(0, -30px)',
              opacity: targetPriceModalAnimating ? 1 : 0,
              maxHeight: '92vh',
              margin: '1rem auto',
              ...(showTargetPriceSuppliers
                ? { maxWidth: '96vw', width: '96vw' }
                : {})
            }}
          >
            <div
              className="modal-content"
              style={{
                maxHeight: '92vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <div className="modal-header flex-shrink-0">
                <h5 className="modal-title mb-0">
                  <i className="fa-solid fa-bullseye mr-2 text-primary" />
                  {showTargetPriceSuppliers
                    ? 'Price Matching & Quote History'
                    : 'Set Price Matching'}
                </h5>
                <button
                  type="button"
                  className="close"
                  aria-label="Close"
                  onClick={() => closeTargetPriceModal()}
                  disabled={isSavingTargetPrice}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  maxHeight: 'calc(92vh - 120px)'
                }}
              >
                {!showTargetPriceSuppliers ? (
                  <>
                    {!lrfqDueDatePassed ? (
                      <div
                        className="alert alert-warning py-2 mb-3"
                        style={{ fontSize: '13px' }}
                      >
                        <i className="fa-solid fa-clock mr-1" />
                        {TARGET_PRICE_BEFORE_DUE_MSG}
                        {rfq?.lrfqDueDate
                          ? ` (Due: ${formatDateTime(rfq.lrfqDueDate)})`
                          : ''}
                      </div>
                    ) : (
                      <div
                        className="alert alert-info py-2 mb-3"
                        style={{ fontSize: '13px' }}
                      >
                        <i className="fa-solid fa-circle-info mr-1" />
                        Saving targets notifies quoted suppliers and gives them
                        4 days (<code>targetPriceDueDate</code>) to
                        counter-offer. Non-quoted suppliers are skipped.
                      </div>
                    )}
                    {renderPriceMatchingEditForm('rfqTargetPrice')}
                  </>
                ) : (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-4 mb-2 mb-md-0">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#eef6fb',
                            borderRadius: '8px',
                            border: '1px solid #d0e4f0'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            Price Matching
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {savedTargetCount > 0
                              ? `${savedTargetCount} line target${
                                  savedTargetCount === 1 ? '' : 's'
                                }`
                              : 'Line targets'}
                            {currencyLabel ? (
                              <span
                                className="text-muted ml-1"
                                style={{ fontSize: '12px', fontWeight: 500 }}
                              >
                                ({lrfqCurrency})
                              </span>
                            ) : null}
                          </div>
                          {boqPriceMatchingLines.length > 0 ? (
                            <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                              {needsMatchingBoqLineCount} of{' '}
                              {boqPriceMatchingLines.length} BOQ line
                              {boqPriceMatchingLines.length === 1 ? '' : 's'} still
                              above target
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-md-4 mb-2 mb-md-0">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#fff8e6',
                            borderRadius: '8px',
                            border: '1px solid #f0e0b2'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            Needs Matching
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {priceMatchingSuppliersList.length}{' '}
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              supplier
                              {priceMatchingSuppliersList.length === 1 ? '' : 's'}
                            </span>
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                            Before history:{' '}
                            {targetPriceHistory.suppliersWithBeforeCount}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div
                          className="p-3 h-100"
                          style={{
                            background: '#e8f7f0',
                            borderRadius: '8px',
                            border: '1px solid #bfe8d4'
                          }}
                        >
                          <div className="text-muted" style={{ fontSize: '12px' }}>
                            At / Below Target
                          </div>
                          <div className="fwt_600" style={{ fontSize: '18px' }}>
                            {matchedSuppliersCount}{' '}
                            <span style={{ fontSize: '13px', fontWeight: 500 }}>
                              supplier
                              {matchedSuppliersCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: '11px' }}>
                            After history:{' '}
                            {targetPriceHistory.suppliersWithAfterCount}
                          </div>
                        </div>
                      </div>
                    </div>

                    <ul className="nav nav-pills mb-3" style={{ gap: '6px' }}>
                      {hasAnyPriceMatchingTarget ? (
                        <li className="nav-item">
                          <button
                            type="button"
                            className={`btn btn-sm ${
                              targetPriceModalTab === 'history'
                                ? 'btn-blue'
                                : 'btn-outline-secondary'
                            }`}
                            onClick={() => {
                              setTargetPriceModalTab('history')
                            }}
                          >
                            <i className="fa-solid fa-clock-rotate-left mr-1" />
                            Quote History
                            {priceMatchingSuppliersList.length > 0 ? (
                              <span className="badge badge-warning ml-1">
                                {priceMatchingSuppliersList.length}
                              </span>
                            ) : null}
                          </button>
                        </li>
                      ) : null}
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            targetPriceModalTab === 'notify'
                              ? 'btn-blue'
                              : 'btn-outline-secondary'
                          }`}
                          onClick={() => {
                            setTargetPriceModalTab('notify')
                          }}
                        >
                          <i className="fa-solid fa-paper-plane mr-1" />
                          Notify Suppliers
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`btn btn-sm ${
                            targetPriceModalTab === 'edit'
                              ? 'btn-blue'
                              : 'btn-outline-secondary'
                          }`}
                          onClick={() => {
                            setTargetPriceModalTab('edit')
                          }}
                        >
                          <i className="fa-solid fa-pen-to-square mr-1" />
                          Edit Value
                        </button>
                      </li>
                      
                      <li className="nav-item">
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={handleExportPriceMatching}
                          disabled={exportingPriceMatching}
                          title="Export Excel"
                        >
                          {exportingPriceMatching ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm mr-1"
                                role="status"
                                aria-hidden="true"
                              />
                              Exporting...
                            </>
                          ) : (
                            <>
                              <i className="fa-solid fa-file-export mr-1" />
                             Export Excel
                            </>
                          )}
                        </button>
                      </li>
                    </ul>

                    {targetPriceModalTab === 'edit' ? (
                      <>
                        {!lrfqDueDatePassed ? (
                          <div
                            className="alert alert-warning py-2 mb-3"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fa-solid fa-clock mr-1" />
                            {TARGET_PRICE_BEFORE_DUE_MSG}
                            {rfq?.lrfqDueDate
                              ? ` (Due: ${formatDateTime(rfq.lrfqDueDate)})`
                              : ''}
                          </div>
                        ) : (
                          <div
                            className="alert alert-info py-2 mb-3"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fa-solid fa-circle-info mr-1" />
                            Saving targets notifies quoted suppliers and gives
                            them 4 days (<code>targetPriceDueDate</code>) to
                            counter-offer. Suppliers who have not quoted cannot
                            update after the main due date.
                          </div>
                        )}
                        {renderPriceMatchingEditForm('rfqTargetPriceEdit')}
                      </>
                    ) : targetPriceModalTab === 'notify' ? (
                      suppliers.length === 0 ? (
                        <div className="txtdsply text-muted mb-0">
                          No suppliers assigned to this LRFQ.
                        </div>
                      ) : (
                        <>
                          <div
                            className="alert alert-info py-2 mb-3"
                            style={{ fontSize: '13px' }}
                          >
                            <i className="fa-solid fa-users mr-1" />
                            All assigned suppliers (
                            <strong>{suppliers.length}</strong>
                            ). Status shows who still needs matching vs already
                            at/below target.
                          </div>
                          <div className="table-responsive mb-0">
                            <table className="table table-bordered table-sm mb-0">
                              <thead>
                                <tr
                                  style={{
                                    backgroundColor: '#003366',
                                    color: '#fff'
                                  }}
                                >
                                  <th style={{ fontSize: '12px' }}>S.No</th>
                                  <th style={{ fontSize: '12px' }}>
                                    Supplier Name
                                  </th>
                                  <th style={{ fontSize: '12px' }}>Email</th>
                                  <th style={{ fontSize: '12px' }}>City</th>
                                  <th style={{ fontSize: '12px' }}>
                                    Phone Number
                                  </th>
                                  <th style={{ fontSize: '12px', width: '110px' }}>
                                    Status
                                  </th>
                                  <th style={{ fontSize: '12px', minWidth: '120px' }}>
                                    PM Due
                                  </th>
                                  <th
                                    style={{ fontSize: '12px', width: '100px' }}
                                  >
                                    Send
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {suppliers.map((row, index) => {
                                  const supplierEmail = String(
                                    row.supplierMailId || ''
                                  ).trim()
                                  const isSending =
                                    sendingPriceMatchFor === supplierEmail
                                  const needsMatching =
                                    priceMatchingSuppliersList.some((pm) =>
                                      suppliersMatchIdentity(pm, row)
                                    ) || supplierNeedsPriceMatching(row)
                                  const hasQuoted = supplierHasQuotedPrice(row)
                                  const targetDue =
                                    getSupplierTargetPriceDueDate(row)
                                  const targetDueActive =
                                    isTargetPriceDueDateActive(targetDue)
                                  return (
                                    <tr
                                      key={
                                        row.supplierId || supplierEmail || index
                                      }
                                    >
                                      <td>{index + 1}</td>
                                      <td
                                        style={{
                                          maxWidth: '220px',
                                          whiteSpace: 'normal',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {row.supplierName || '-'}
                                      </td>
                                      <td>{row.supplierMailId || '-'}</td>
                                      <td>{row.city || '-'}</td>
                                      <td>
                                        {row.phoneNumber ||
                                          row.phonenumber ||
                                          '-'}
                                      </td>
                                      <td className="text-center">
                                        {!hasQuoted ? (
                                          <span className="badge badge-secondary">
                                            No quote
                                          </span>
                                        ) : needsMatching ? (
                                          <span className="badge badge-warning">
                                            Needs matching
                                          </span>
                                        ) : (
                                          <span className="badge badge-success">
                                            At / below
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ fontSize: '12px' }}>
                                        {targetDue ? (
                                          <>
                                            <div>
                                              {formatDateTime(targetDue)}
                                            </div>
                                            <span
                                              className={`badge ${
                                                targetDueActive
                                                  ? 'badge-success'
                                                  : 'badge-secondary'
                                              }`}
                                              style={{ fontSize: '10px' }}
                                            >
                                              {targetDueActive
                                                ? 'Open'
                                                : 'Closed'}
                                            </span>
                                          </>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td className="text-center">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          title="Send price matching email"
                                          onClick={() =>
                                            handleSendPriceMatchingEmail(row)
                                          }
                                          disabled={
                                            !supplierEmail ||
                                            Boolean(sendingPriceMatchFor)
                                          }
                                        >
                                          {isSending ? (
                                            <i className="fa-solid fa-spinner fa-spin" />
                                          ) : (
                                            <i className="fa-solid fa-paper-plane" />
                                          )}
                                        </button>
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          </div>
                        </>
                      )
                    ) : !hasAnyPriceMatchingTarget ? (
                      <div className="txtdsply text-muted mb-0">
                        No target price set yet. Use Edit Value to add price
                        matching targets first.
                      </div>
                    ) : boqPriceMatchingLines.length === 0 ? (
                      <div className="txtdsply text-muted mb-0">
                        No BOQ quoted vs target data yet. Set price matching
                        targets and wait for supplier quotes.
                      </div>
                    ) : (
                      <div>
                        <div
                          className="px-3 py-2 fwt_600 d-flex justify-content-between align-items-center"
                          style={{
                            background: '#eef6fb',
                            border: '1px solid #d0e4f0',
                            borderBottom: 'none',
                            borderRadius: '8px 8px 0 0',
                            fontSize: '13px'
                          }}
                        >
                          <span>
                            <i className="fa-solid fa-table mr-1" />
                            Quote &amp; Target History
                            <span
                              className="text-muted ml-2"
                              style={{ fontSize: '12px', fontWeight: 500 }}
                            >
                              (1st quoted → targets → revisions)
                            </span>
                          </span>
                          <span
                            className="text-muted"
                            style={{ fontSize: '12px', fontWeight: 500 }}
                          >
                            {needsMatchingBoqLineCount} line
                            {needsMatchingBoqLineCount === 1 ? '' : 's'} need
                            matching
                          </span>
                        </div>
                        <div
                          className="table-responsive"
                          style={{
                            border: '1px solid #d0e4f0',
                            borderRadius: '0 0 8px 8px',
                            maxHeight: '42vh',
                            overflowY: 'auto',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch'
                          }}
                        >
                          <table
                            className="table table-bordered table-sm mb-0"
                            style={{ minWidth: '1400px' }}
                          >
                            <thead
                              style={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 2
                              }}
                            >
                              <tr
                                style={{
                                  backgroundColor: '#003366',
                                  color: '#fff'
                                }}
                              >
                                {[
                                  'S.No',
                                  'Cluster',
                                  'Cluster Description',
                                  'HSN',
                                  'UOM',
                                  'Qty',
                                  'Supplier',
                                  ...priceMatchingHistoryColumns.map(
                                    (col) => col.label
                                  ),
                                  'Difference',
                                  'Status'
                                ].map((label) => (
                                  <th
                                    key={label}
                                    style={{
                                      fontSize: '12px',
                                      whiteSpace: 'nowrap',
                                      backgroundColor: '#003366'
                                    }}
                                  >
                                    {label}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {priceMatchingHistorySupplierSections.flatMap(
                                (section) =>
                                  priceMatchingHistoryGroups.flatMap((group) => {
                                    const sectionSupplier = section.supplier
                                    const parentSupplier =
                                      sectionSupplier || group.supplier
                                    const parentHistory = getParentPriceMatchingHistoryValues(
                                      rfq,
                                      parentSupplier,
                                      group,
                                      priceMatchingHistoryColumns
                                    )
                                    const parentHistoryValues =
                                      parentHistory.values || parentHistory
                                    const parentHistoryFormulas =
                                      parentHistory.formulas || {}
                                    const parentSupplierLabel =
                                      sectionSupplier?.supplierName ||
                                      sectionSupplier?.supplierMailId ||
                                      group.supplierName ||
                                      '—'
                                    const parentRow = (
                                      <tr
                                        key={`pm-parent-${section.key}-${group.parentNo}`}
                                        style={{ backgroundColor: '#fff' }}
                                      >
                                        <td className="fwt_600">
                                          {group.parentNo}
                                          {group.hasBoqChildren ? (
                                            <span
                                              className="badge badge-warning ml-1"
                                              style={{ fontSize: '9px' }}
                                            >
                                              BOQ
                                            </span>
                                          ) : null}
                                        </td>
                                        <td>{group.sourceCode || '-'}</td>
                                        <td
                                          style={{
                                            maxWidth: '220px',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word'
                                          }}
                                        >
                                          {group.sourceDescription || '-'}
                                        </td>
                                        <td>{group.hsn || '-'}</td>
                                        <td>{group.uom || '-'}</td>
                                        <td>{group.quantity ?? '-'}</td>
                                        <td>{parentSupplierLabel}</td>
                                        {priceMatchingHistoryColumns.map(
                                          (col) => {
                                            const value =
                                              parentHistoryValues[col.key]
                                            const formula =
                                              parentHistoryFormulas[col.key] ||
                                              ''
                                            const isRevision =
                                              col.kind === 'revision'
                                            const cellKey = `pm-parent-${section.key}-${group.parentNo}-${col.key}`
                                            const amount = Number.isFinite(
                                              value
                                            )
                                              ? value
                                              : null
                                            if (col.kind === 'target') {
                                              return renderParentTotalTargetCell(
                                                amount,
                                                'text-right fwt_600',
                                                cellKey,
                                                formula
                                              )
                                            }
                                            if (
                                              col.kind === 'initial' ||
                                              col.kind === 'revision'
                                            ) {
                                              return renderParentTotalBasicCell(
                                                amount,
                                                `text-right${isRevision ? ' fwt_600' : ''}`,
                                                cellKey,
                                                formula
                                              )
                                            }
                                            return (
                                              <td
                                                key={cellKey}
                                                className="text-center text-muted"
                                              >
                                                —
                                              </td>
                                            )
                                          }
                                        )}
                                        <td className="text-center text-muted">
                                          —
                                        </td>
                                        <td className="text-center text-muted">
                                          —
                                        </td>
                                      </tr>
                                    )

                                    const childRows = group.lines.flatMap(
                                      (line, lineIdx) => {
                                        const supplierEntries =
                                          getBoqLineSupplierEntries(line)
                                        const entries = sectionSupplier
                                          ? supplierEntries.filter((entry) =>
                                              suppliersMatchIdentity(
                                                entry,
                                                sectionSupplier
                                              )
                                            )
                                          : supplierEntries.length > 0
                                            ? supplierEntries
                                            : [null]
                                        const isBoqChild = Boolean(
                                          line.isBoqChild
                                        )

                                        if (
                                          sectionSupplier &&
                                          entries.length === 0
                                        ) {
                                          return []
                                        }

                                        return entries.map(
                                          (entry, entryIdx) => {
                                            const supplier = entry
                                              ? suppliers.find((s) =>
                                                  suppliersMatchIdentity(
                                                    s,
                                                    entry
                                                  )
                                                ) || entry
                                              : null
                                            const history = entry
                                              ? getLrfqPriceMatchingHistoryCellValues(
                                                  rfq,
                                                  supplier,
                                                  entry,
                                                  line,
                                                  priceMatchingHistoryColumns
                                                )
                                              : {
                                                  values: {},
                                                  lastQuoted: NaN,
                                                  lastTarget: NaN,
                                                  difference: NaN
                                                }
                                            const diff = history.difference
                                            const needsMatch =
                                              Number.isFinite(diff) && diff > 0
                                            const qty =
                                              entry?.quantity != null
                                                ? entry.quantity
                                                : line.quantity

                                            // Non-BOQ single material: skip duplicate parent.
                                            if (
                                              !isBoqChild &&
                                              group.hasBoqChildren
                                            ) {
                                              return null
                                            }

                                            return (
                                              <tr
                                                key={`pm-line-${section.key}-${line.boqNo || lineIdx}-${entry?.supplierId || entry?.supplierMailId || entryIdx}`}
                                                style={{
                                                  backgroundColor: needsMatch
                                                    ? '#fff8e6'
                                                    : isBoqChild
                                                      ? '#fff8e6'
                                                      : '#fff'
                                                }}
                                              >
                                                <td
                                                  className="fwt_600"
                                                  style={{
                                                    color: isBoqChild
                                                      ? '#856404'
                                                      : undefined,
                                                    paddingLeft: isBoqChild
                                                      ? '14px'
                                                      : undefined
                                                  }}
                                                >
                                                  {isBoqChild
                                                    ? line.boqNo || '-'
                                                    : group.parentNo}
                                                </td>
                                                <td
                                                  className={
                                                    isBoqChild
                                                      ? 'text-muted'
                                                      : ''
                                                  }
                                                >
                                                  {isBoqChild
                                                    ? '—'
                                                    : line.sourceCode ||
                                                      group.sourceCode ||
                                                      '-'}
                                                </td>
                                                <td
                                                  style={{
                                                    maxWidth: '220px',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-word'
                                                  }}
                                                >
                                                  {isBoqChild
                                                    ? line.materialDescription ||
                                                      line.description ||
                                                      '-'
                                                    : line.materialDescription ||
                                                      group.sourceDescription ||
                                                      '-'}
                                                </td>
                                                <td
                                                  className={
                                                    isBoqChild
                                                      ? 'text-muted'
                                                      : ''
                                                  }
                                                >
                                                  {isBoqChild
                                                    ? '—'
                                                    : line.hsn ||
                                                      group.hsn ||
                                                      '-'}
                                                </td>
                                                <td
                                                  className={
                                                    isBoqChild
                                                      ? 'text-muted'
                                                      : ''
                                                  }
                                                >
                                                  {isBoqChild
                                                    ? '—'
                                                    : line.uom ||
                                                      group.uom ||
                                                      '-'}
                                                </td>
                                                <td>{qty ?? '-'}</td>
                                                <td>
                                                  {entry?.supplierName ||
                                                    entry?.supplierMailId ||
                                                    '-'}
                                                </td>
                                                {priceMatchingHistoryColumns.map(
                                                  (col) => {
                                                    const value =
                                                      history.values[col.key]
                                                    const isTarget =
                                                      col.kind === 'target'
                                                    const isRevision =
                                                      col.kind === 'revision'
                                                    return (
                                                      <td
                                                        key={col.key}
                                                        className={`text-right${
                                                          isTarget ||
                                                          isRevision
                                                            ? ' fwt_600'
                                                            : ''
                                                        }${isTarget ? ' text-primary' : ''}`}
                                                      >
                                                        {Number.isFinite(value)
                                                          ? formatAmount(value)
                                                          : '-'}
                                                      </td>
                                                    )
                                                  }
                                                )}
                                                <td
                                                  className="text-right fwt_600"
                                                  style={{
                                                    color: needsMatch
                                                      ? '#c82333'
                                                      : Number.isFinite(diff) &&
                                                          diff < 0
                                                        ? '#1e7e34'
                                                        : '#6c757d'
                                                  }}
                                                >
                                                  {Number.isFinite(diff)
                                                    ? formatSignedAmount(diff)
                                                    : '-'}
                                                </td>
                                                <td className="text-center">
                                                  {entry ? (
                                                    needsMatch ? (
                                                      <span className="badge badge-warning">
                                                        Needs matching
                                                      </span>
                                                    ) : Number.isFinite(
                                                        diff
                                                      ) ? (
                                                      <span className="badge badge-success">
                                                        At / below
                                                      </span>
                                                    ) : (
                                                      <span className="text-muted">
                                                        —
                                                      </span>
                                                    )
                                                  ) : (
                                                    <span className="text-muted">
                                                      —
                                                    </span>
                                                  )}
                                                </td>
                                              </tr>
                                            )
                                          }
                                        )
                                      }
                                    )

                                    const visibleChildren =
                                      childRows.filter(Boolean)
                                    // Skip empty supplier/group blocks (no quoted lines).
                                    if (
                                      sectionSupplier &&
                                      visibleChildren.length === 0
                                    ) {
                                      return []
                                    }
                                    if (group.hasBoqChildren) {
                                      return [
                                        parentRow,
                                        ...visibleChildren
                                      ]
                                    }
                                    return visibleChildren
                                  })
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer flex-shrink-0">
                {!showTargetPriceSuppliers || targetPriceModalTab === 'edit' ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() =>
                        showTargetPriceSuppliers
                          ? setTargetPriceModalTab('notify')
                          : closeTargetPriceModal()
                      }
                      disabled={isSavingTargetPrice}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-blue"
                      onClick={handleSaveTargetPrice}
                      disabled={isSavingTargetPrice || !canEditTargetPrice}
                      title={
                        !canEditTargetPrice && canUsePriceMatching
                          ? TARGET_PRICE_BEFORE_DUE_MSG
                          : undefined
                      }
                    >
                      {isSavingTargetPrice ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm mr-1"
                            role="status"
                            aria-hidden="true"
                          />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-floppy-disk mr-1" />
                          Save
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => closeTargetPriceModal()}
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {showTargetPriceModal ? (
        <div
          className={`modal-backdrop fade${targetPriceModalAnimating ? ' show' : ''}`}
        />
      ) : null}
    </div>
  )
}

export default LrfqView
